import { createTooltipApi } from "./tooltip";
import { renderMarkdown } from "./markdown-lite.mjs";
/**
 * ArcLog OS UI kit (DOM) — macOS-glass primitives for SDK apps.
 * Exposed as ctx.ui from the host (do not re-implement in each app).
 */
import type { IconsApi } from "./icons-host";
import type {
  UiApi,
  UiColorOptions,
  UiNumberOptions,
  UiSelectHandle,
  UiSelectOption,
  UiSelectOptions,
  UiSliderOptions,
  UiTextOptions,
  UiToggleOptions,
  UiValueHandle,
} from "@arclog/module-sdk";
import {
  createCodeEditor,
  getEditorPrefs,
  setEditorPrefs,
  subscribeEditorPrefs,
  type CodeEditorHandle,
} from "./code-editor";

export type UiTreeNode = {
  id: string;
  label: string;
  icon?: string;
  children?: UiTreeNode[];
  data?: unknown;
  /** default expanded when children exist */
  expanded?: boolean;
};

export type UiTreeHandle = {
  el: HTMLElement;
  setData(nodes: UiTreeNode[]): void;
  getSelected(): string | null;
  setSelected(id: string | null): void;
  expand(id: string, open?: boolean): void;
  destroy(): void;
};

export type UiTreeOptions = {
  className?: string;
  /** multi-level indent px */
  indent?: number;
  onSelect?: (id: string, node: UiTreeNode) => void;
  onActivate?: (id: string, node: UiTreeNode) => void;
  onContextMenu?: (id: string, node: UiTreeNode, ev: MouseEvent) => void;
  /** flash ephemeral highlight */
  flashIds?: string[];
};

export type UiSplitHandle = {
  el: HTMLElement;
  setSizes(sizes: number[]): void;
  getSizes(): number[];
  destroy(): void;
};

export type UiSplitOptions = {
  direction?: "horizontal" | "vertical";
  /** flex sizes (relative). length = panes */
  sizes?: number[];
  minSize?: number;
  className?: string;
  storageKey?: string;
  onResize?: (sizes: number[]) => void;
};

export type UiSearchSelectOption = {
  value: string;
  label: string;
  hint?: string;
  group?: string;
};

export type UiSearchSelectHandle = {
  el: HTMLElement;
  getValue(): string;
  setValue(v: string): void;
  setOptions(opts: UiSearchSelectOption[]): void;
  destroy(): void;
};

export type UiSearchSelectOptions = {
  options: UiSearchSelectOption[];
  value?: string;
  placeholder?: string;
  className?: string;
  onChange?: (value: string, opt: UiSearchSelectOption | undefined) => void;
};

export type UiCommand = {
  id: string;
  label: string;
  /** Secondary text on the right (a path, a description). */
  hint?: string;
  /** Section header the command is listed under. */
  group?: string;
  /** Lucide icon id. */
  icon?: string;
  /** Display-only shortcut badge, e.g. "⌘S". */
  shortcut?: string;
  /** Extra words the filter should match on. */
  keywords?: string;
  disabled?: boolean;
  run: () => void;
};

export type UiCommandPaletteHandle = {
  open(query?: string): void;
  close(): void;
  toggle(): void;
  isOpen(): boolean;
  setCommands(commands: UiCommand[]): void;
  destroy(): void;
};

export type UiCommandPaletteOptions = {
  /** Static command list; may also be supplied later via setCommands. */
  commands?: UiCommand[];
  placeholder?: string;
  /**
   * Dynamic results for the current query (file search, symbols…). Merged
   * after the static matches. Called on every keystroke; keep it cheap.
   */
  onQuery?: (query: string) => UiCommand[] | Promise<UiCommand[]>;
  /** Shown when nothing matches. */
  emptyText?: string;
  /**
   * Element the ⌘⇧P / Ctrl+⇧P listener is bound to (default: document). Pass the
   * app root so the palette only opens while that app has focus.
   */
  hotkeyTarget?: HTMLElement | Document;
  /** Bind the default ⌘⇧P / Ctrl+⇧P hotkey. Default true. */
  hotkey?: boolean;
};

export type UiTab = {
  id: string;
  label: string;
  /** Tooltip — usually the full path the short label came from. */
  hint?: string;
  /** Lucide icon id. */
  icon?: string;
  /** Unsaved: shows a dot that becomes the close × on hover. */
  dirty?: boolean;
  /** Default true when an onClose handler is supplied. */
  closable?: boolean;
};

export type UiTabsHandle = {
  el: HTMLElement;
  setTabs(tabs: UiTab[]): void;
  setActive(id: string | null): void;
  getActive(): string | null;
  destroy(): void;
};

export type UiTabsOptions = {
  tabs?: UiTab[];
  activeId?: string | null;
  className?: string;
  onSelect?: (id: string) => void;
  /** Supply to make tabs closable — via the ×, or middle-click. */
  onClose?: (id: string) => void;
};

export type UiStatusBarItem = {
  id: string;
  label: string;
  icon?: string;
  title?: string;
  align?: "left" | "right";
  tone?: "default" | "ok" | "warn" | "err" | "accent";
  onClick?: () => void;
};

export type UiStatusBarHandle = {
  el: HTMLElement;
  /** Add or update one item in place (unknown id appends it). */
  set(item: UiStatusBarItem): void;
  /** Replace every item. */
  setItems(items: UiStatusBarItem[]): void;
  remove(id: string): void;
  destroy(): void;
};

const STYLE_ID = "arclog-ui-kit-css";

function ensureCss() {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = `
/* ── ctx.ui control kit ───────────────────────────────────────────────────
   Values come from the --os-* scales in styles/os.css. A literal below is a
   decision, and the comment beside it says what the decision was.
   Three house rules, all learned the hard way:
     · Every selector is prefixed .os-. This sheet is injected into the
       document head, so a bare button{} would restyle the whole OS.
     · Nothing clickable ships without a :focus-visible ring. outline:none
       never appears on its own — it is always paired with a ring on the
       same element (or on the wrapper that owns the focus, where the
       focusable node is visually hidden).
     · Nothing clickable presents a hit target under 32px. Where the paint
       is deliberately smaller (a colour chip, a tab close), the hit area is
       grown with a pseudo-element rather than by inflating the visual.
   Rings are box-shadow rather than outline so they follow border-radius on
   every engine; inside a container with overflow:hidden the ring is inset
   instead, because an outer ring there is simply clipped away. */
.os-tree{font:var(--os-text-base,12px) var(--os-font-ui,system-ui);color:var(--os-text,#e8eaef);user-select:none;outline:none}
/* The tree root is the focusable element (rows are painted selection), so
   the ring lives here and is inset — a tree usually fills its pane edge to
   edge and an outer ring would be clipped by the scroll container. */
.os-tree:focus-visible{outline:none;box-shadow:inset 0 0 0 2px var(--os-accent-soft,rgba(10,132,255,.28))}
/* 32px rows. 24px was a fine mouse target and a poor trackpad/touch one, and
   the tree is the most-clicked surface in the kit, so it pays the density
   cost first. cursor:pointer because cursor:default read as "not clickable"
   on a row that is the entire point of the control. */
.os-tree-row{display:flex;align-items:center;gap:var(--os-space-1,4px);padding:var(--os-space-1,4px) var(--os-space-3,8px) var(--os-space-1,4px) var(--os-space-1,4px);border-radius:var(--os-radius-sm,6px);cursor:pointer;min-height:32px;transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-tree-row:hover{background:var(--os-hover,rgba(255,255,255,.08))}
.os-tree-row:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-tree-row.is-selected{background:var(--os-accent-soft,rgba(10,132,255,.28))}
.os-tree-row.is-flash{box-shadow:inset 0 0 0 1px var(--os-success,#30d158)}
.os-tree-twist{position:relative;width:16px;height:16px;display:grid;place-items:center;flex-shrink:0;opacity:.7;border:none;background:transparent;color:inherit;padding:0;cursor:pointer;border-radius:var(--os-radius-sm,6px);transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease),opacity var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
/* The chevron paints at 16px — bigger and it reads as a second icon — so the
   target is grown with a pseudo-element. It grows vertically only (to the
   full 32px row): horizontal bleed would sit over the label and swallow the
   row's own select click. */
.os-tree-twist::after{content:"";position:absolute;left:0;right:0;top:-8px;bottom:-8px}
.os-tree-twist:hover{background:var(--os-hover,rgba(255,255,255,.08));opacity:1}
.os-tree-twist:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-tree-twist:focus-visible{outline:none;opacity:1;box-shadow:0 0 0 2px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-tree-twist.is-leaf{opacity:0;pointer-events:none}
.os-tree-ico{width:16px;height:16px;display:grid;place-items:center;flex-shrink:0;opacity:.9}
.os-tree-label{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:var(--os-text-base,12px);letter-spacing:-.01em}
.os-tree-children{/* indent handled per-row padding */}
.os-split{display:flex;width:100%;height:100%;min-height:0;min-width:0}
.os-split.is-vertical{flex-direction:column}
.os-split-pane{min-width:0;min-height:0;overflow:hidden;display:flex;flex-direction:column;position:relative}
/* A resize gutter is a drag affordance, not a click target: the cursor and
   the 5px band are the platform convention and a 32px one would eat layout. */
.os-split-gutter{flex:0 0 5px;background:transparent;position:relative;z-index:2;cursor:col-resize}
.os-split.is-vertical .os-split-gutter{cursor:row-resize;flex-basis:5px}
.os-split-gutter::after{content:"";position:absolute;inset:0;background:rgba(255,255,255,0);transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-split-gutter:hover::after,.os-split-gutter.is-active::after{background:var(--os-accent,#0a84ff)}
.os-split.is-horizontal .os-split-gutter::after{width:1px;left:2px;right:auto;top:0;bottom:0}
.os-split.is-vertical .os-split-gutter::after{height:1px;top:2px;bottom:auto;left:0;right:0}
.os-sselect{position:relative;min-width:160px;font:var(--os-text-base,12px) var(--os-font-ui,system-ui);color:var(--os-text,#e8eaef)}
.os-sselect-btn{width:100%;min-height:32px;box-sizing:border-box;display:flex;align-items:center;gap:var(--os-space-3,8px);text-align:left;border:1px solid var(--os-border-default,rgba(255,255,255,.14));background:var(--os-surface-2,rgba(255,255,255,.06));color:inherit;border-radius:var(--os-radius-md,10px);padding:var(--os-space-2,6px) var(--os-space-4,12px);cursor:pointer;font:inherit;transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease),border-color var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-sselect-btn:hover{background:var(--os-hover,rgba(255,255,255,.08))}
.os-sselect-btn:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-sselect-btn:disabled{opacity:.45;cursor:not-allowed}
.os-sselect-btn:focus-visible{outline:none;border-color:var(--os-accent,#0a84ff);box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-sselect-btn .val{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.os-sselect-btn .chev{opacity:.5;font-size:var(--os-text-xs,10px)}
/* The two menus below (this popover and the command palette) named
   --os-glass-bg-strong but wrote rgba(28,30,38,.94) and .96 as their fallback.
   The token is .72 — the fallback was not the token, it was the value someone
   actually wanted, kept alive in the slot meant for "if the token is missing".
   --os-glass-bg-heavy IS rgba(28,30,38,.94), and it was added for exactly this:
   an opaque menu you read text off, not the translucent glass of a panel. Both
   now name it, so these menus render the .94 they were drawn against instead
   of the .72 the token actually resolved to. The palette's .96 goes to .94 —
   two hundredths, and one less depth of glass in the OS. */
.os-sselect-pop{position:absolute;top:calc(100% + var(--os-space-1,4px));left:0;right:0;min-width:280px;max-height:320px;z-index:80;display:flex;flex-direction:column;border-radius:var(--os-radius-lg,14px);border:1px solid var(--os-border-default,rgba(255,255,255,.14));background:var(--os-glass-bg-heavy,rgba(28,30,38,.94));backdrop-filter:blur(var(--os-blur-md,28px)) saturate(160%);-webkit-backdrop-filter:blur(var(--os-blur-md,28px)) saturate(160%);box-shadow:var(--os-elev-2,0 10px 30px rgba(0,0,0,.38));overflow:hidden}
.os-sselect-search{min-height:32px;box-sizing:border-box;border:none;border-bottom:1px solid var(--os-border-subtle,rgba(255,255,255,.08));background:transparent;color:inherit;padding:var(--os-space-3,8px) var(--os-space-4,12px);font:var(--os-text-base,12px) var(--os-font-ui,system-ui);outline:none}
/* The popover clips (overflow:hidden), so this field rings itself with its
   own underline rather than a shadow that would be cut off at the edge. */
.os-sselect-search:focus-visible{outline:none;border-bottom-color:var(--os-accent,#0a84ff);box-shadow:inset 0 -1px 0 0 var(--os-accent,#0a84ff)}
.os-sselect-list{overflow:auto;padding:var(--os-space-1,4px);flex:1}
.os-sselect-opt{width:100%;min-height:32px;box-sizing:border-box;display:flex;flex-direction:column;justify-content:center;align-items:flex-start;gap:1px;border:none;background:transparent;color:inherit;text-align:left;padding:var(--os-space-2,6px) var(--os-space-4,12px);border-radius:var(--os-radius-md,10px);cursor:pointer;font:var(--os-text-base,12px) var(--os-font-ui,system-ui)}
.os-sselect-opt:hover,.os-sselect-opt.is-active{background:var(--os-accent-soft,rgba(10,132,255,.28))}
.os-sselect-opt:active{background:var(--os-accent,#0a84ff)}
/* inset: the option list scrolls inside a clipped popover */
.os-sselect-opt:focus-visible{outline:none;box-shadow:inset 0 0 0 2px var(--os-accent,#0a84ff)}
.os-sselect-opt .lab{font-weight:600}
.os-sselect-opt .hint{font-size:var(--os-text-xs,10px);opacity:.5;font-family:var(--os-font-mono,ui-monospace,monospace)}
.os-sselect-empty{padding:var(--os-space-5,16px);text-align:center;opacity:.45;font-size:var(--os-text-base,12px)}
.os-sselect-group{padding:var(--os-space-3,8px) var(--os-space-4,12px) var(--os-space-1,4px);font-size:var(--os-text-xs,10px);font-weight:650;letter-spacing:.05em;text-transform:uppercase;opacity:.4}
/* --os-well-2: a hole in the pane behind it. The surface scale is white tints
   and can only go lighter, so "darker than my parent" used to have no token
   and every well here hardcoded its own black alpha; --os-well-1/2 are that
   token now. The other wells below still carry their literals — each is a
   different alpha and picking a depth for them is a judgement call, not a
   substitution. */
.os-code-ed{position:relative;display:flex;flex-direction:column;flex:1;min-height:0;height:100%;background:var(--os-well-2,rgba(0,0,0,.28));overflow:hidden;transition:box-shadow var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
/* The textarea fills the well, so the well carries its ring — an outer ring
   on a full-bleed textarea has nowhere to draw. */
.os-code-ed:focus-within{box-shadow:inset 0 0 0 2px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-code-ed-bar{display:flex;align-items:center;gap:var(--os-space-3,8px);padding:var(--os-space-1,4px) var(--os-space-4,12px);font:var(--os-text-xs,10px) var(--os-font-mono,ui-monospace,monospace);opacity:.55;border-bottom:1px solid var(--os-border-subtle,rgba(255,255,255,.08));background:rgba(0,0,0,.15)}
.os-code-ed-body{position:relative;flex:1;min-height:0;overflow:auto}
.os-code-ed pre,.os-code-ed textarea{margin:0;border:0;padding:var(--os-space-5,16px);font:var(--os-text-base,12px)/1.55 var(--os-font-mono,ui-monospace,Menlo,monospace);tab-size:2;white-space:pre;box-sizing:border-box;width:100%;min-height:100%}
.os-code-ed pre{position:absolute;inset:0;pointer-events:none;color:var(--os-code-fg,#d7dde8);background:transparent;z-index:1}
.os-code-ed textarea{position:relative;z-index:2;background:transparent;color:transparent;caret-color:var(--os-accent,#0a84ff);resize:none;outline:none}
/* The four token colours were GitHub-dark literals that no theme could
   reach. They are custom properties now, declared on :root at those same
   values and mirrored into FALLBACK_THEME_VARS — a theme recolours the
   highlighter, and switching away puts it back. The literals stay here as
   fallbacks for the same reason every other read in this file keeps one:
   these styles are injected into contexts that may not have os.css. */
.os-code-ed .tok-kw{color:var(--os-code-kw,#ff7b72)}.os-code-ed .tok-str{color:var(--os-code-str,#a5d6ff)}.os-code-ed .tok-com{color:var(--os-code-com,#8b949e);font-style:italic}.os-code-ed .tok-num{color:var(--os-code-num,#79c0ff)}
.os-rte{display:flex;flex-direction:column;flex:1;min-height:0;height:100%;background:rgba(0,0,0,.18)}
.os-rte-tb{display:flex;flex-wrap:wrap;gap:var(--os-space-1,4px);padding:var(--os-space-2,6px) var(--os-space-3,8px);border-bottom:1px solid var(--os-border-subtle,rgba(255,255,255,.08));background:var(--os-surface-1,rgba(255,255,255,.03))}
/* 32×32 minimum: these were 28px wide and 24px tall, which is a coin-flip
   target for "bold" on a trackpad. */
.os-rte-tb button{border:1px solid var(--os-border-default,rgba(255,255,255,.14));background:var(--os-surface-2,rgba(255,255,255,.06));color:inherit;border-radius:var(--os-radius-sm,6px);padding:0 var(--os-space-3,8px);font:var(--os-text-sm,11px) var(--os-font-ui,system-ui);cursor:pointer;min-width:32px;min-height:32px;transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-rte-tb button:hover{background:var(--os-hover,rgba(255,255,255,.08))}
.os-rte-tb button:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-rte-tb button:disabled{opacity:.45;cursor:not-allowed}
.os-rte-tb button:focus-visible{outline:none;border-color:var(--os-accent,#0a84ff);box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-rte-tb .sep{width:1px;background:var(--os-border-default,rgba(255,255,255,.14));margin:2px var(--os-space-1,4px)}
.os-rte-body{flex:1;min-height:0;overflow:auto;padding:var(--os-space-5,16px);outline:none;font:var(--os-text-lg,15px)/1.55 var(--os-font-ui,system-ui);color:var(--os-text,#fff)}
/* contenteditable: the body is the focusable node and it fills its pane, so
   the ring is inset for the same reason the code well's is. */
.os-rte-body:focus-visible{outline:none;box-shadow:inset 0 0 0 2px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-rte-body:empty:before{content:attr(data-placeholder);opacity:.35}
.os-rte-body h1{font-size:1.6em;margin:.4em 0}.os-rte-body h2{font-size:1.3em;margin:.4em 0}
.os-rte-body ul,.os-rte-body ol{padding-left:1.4em}
.os-sidebar{display:flex;flex-direction:column;height:100%;min-height:0;background:var(--os-surface-1,rgba(255,255,255,.03));border-right:1px solid var(--os-border-subtle,rgba(255,255,255,.08));padding:var(--os-space-4,12px) var(--os-space-3,8px);box-sizing:border-box;overflow:auto;flex:0 0 auto}
.os-sidebar-search{width:100%;min-height:32px;box-sizing:border-box;border:1px solid var(--os-border-default,rgba(255,255,255,.14));background:var(--os-surface-2,rgba(255,255,255,.06));color:inherit;border-radius:var(--os-radius-md,10px);padding:var(--os-space-2,6px) var(--os-space-4,12px);font:var(--os-text-base,12px) var(--os-font-ui,system-ui);margin-bottom:var(--os-space-3,8px);outline:none}
.os-sidebar-search:focus,.os-sidebar-search:focus-visible{outline:none;border-color:var(--os-accent,#0a84ff);box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-sidebar-group{font-size:var(--os-text-xs,10px);font-weight:700;letter-spacing:.06em;text-transform:uppercase;opacity:.4;padding:var(--os-space-4,12px) var(--os-space-3,8px) var(--os-space-1,4px)}
.os-sidebar-item{display:flex;align-items:center;gap:var(--os-space-3,8px);width:100%;min-height:32px;box-sizing:border-box;border:none;background:transparent;color:inherit;text-align:left;padding:var(--os-space-2,6px) var(--os-space-4,12px);border-radius:var(--os-radius-md,10px);cursor:pointer;font:var(--os-text-md,13px) var(--os-font-ui,system-ui);margin-bottom:2px;transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-sidebar-item:hover{background:var(--os-hover,rgba(255,255,255,.08))}
.os-sidebar-item:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-sidebar-item.is-on{background:var(--os-accent-soft,rgba(10,132,255,.28));font-weight:600}
.os-sidebar-item.is-disabled{opacity:.35;cursor:not-allowed}
.os-sidebar-item:focus-visible{outline:none;box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-sidebar-item .ico{width:18px;height:18px;display:grid;place-items:center;flex-shrink:0;opacity:.9}
.os-sidebar-item .lab{flex:1;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.os-sidebar-item .hint{font-size:var(--os-text-xs,10px);opacity:.45}
.os-btn{display:inline-flex;align-items:center;justify-content:center;gap:var(--os-space-2,6px);min-height:32px;box-sizing:border-box;border:1px solid var(--os-border-default,rgba(255,255,255,.14));background:var(--os-surface-2,rgba(255,255,255,.06));color:inherit;border-radius:var(--os-radius-md,10px);padding:var(--os-space-2,6px) var(--os-space-4,12px);font:var(--os-text-md,13px) var(--os-font-ui,system-ui);font-weight:550;cursor:pointer;transition:filter var(--os-dur-fast,120ms) var(--os-ease-out,ease),background var(--os-dur-fast,120ms) var(--os-ease-out,ease),border-color var(--os-dur-fast,120ms) var(--os-ease-out,ease),transform var(--os-dur-fast,120ms) var(--os-ease-out,ease);white-space:nowrap}
.os-btn:hover{background:var(--os-hover,rgba(255,255,255,.08))}
.os-btn:active{background:var(--os-active,rgba(255,255,255,.14));transform:scale(.98)}
.os-btn:disabled{opacity:.45;cursor:not-allowed}
.os-btn:disabled:hover{background:var(--os-surface-2,rgba(255,255,255,.06))}
.os-btn:focus-visible{outline:none;box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.35))}
.os-btn svg{width:15px;height:15px}
/* Flat. The 180deg gradient plus a coloured drop shadow was the most dated
   thing in the kit — the accent is the identity, so it is the whole fill,
   and the only shadow a button gets now is its focus ring. */
.os-btn-primary{background:var(--os-accent,#0a84ff);border-color:transparent;color:#fff}
.os-btn-primary:hover{background:var(--os-accent,#0a84ff);filter:brightness(1.08)}
.os-btn-primary:active{background:var(--os-accent,#0a84ff);filter:brightness(.94)}
.os-btn-primary:disabled:hover{background:var(--os-accent,#0a84ff);filter:none}
/* Danger reads from --os-danger instead of the old lightened #ff6b61 twin,
   so a theme that recolours danger recolours this button too. color-mix
   supplies the tints an alpha-less token cannot. */
.os-btn-danger{background:color-mix(in srgb,var(--os-danger,#ff453a) 16%,transparent);border-color:color-mix(in srgb,var(--os-danger,#ff453a) 38%,transparent);color:var(--os-danger,#ff453a)}
.os-btn-danger:hover{background:color-mix(in srgb,var(--os-danger,#ff453a) 26%,transparent)}
.os-btn-danger:active{background:color-mix(in srgb,var(--os-danger,#ff453a) 34%,transparent)}
.os-btn-danger:focus-visible{outline:none;box-shadow:0 0 0 3px color-mix(in srgb,var(--os-danger,#ff453a) 35%,transparent)}
.os-btn-ghost{background:transparent;border-color:transparent}
.os-btn-ghost:hover{background:var(--os-hover,rgba(255,255,255,.08))}
.os-btn-ghost:disabled:hover{background:transparent}
.os-modal-scrim{position:fixed;inset:0;z-index:400;display:grid;place-items:center;background:rgba(0,0,0,.4);backdrop-filter:blur(var(--os-blur-sm,6px));animation:osFade var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
@keyframes osFade{from{opacity:0}to{opacity:1}}
.os-modal{width:min(360px,92vw);border-radius:var(--os-radius-xl,18px);border:1px solid var(--os-border-default,rgba(255,255,255,.14));background:rgba(26,30,40,.98);box-shadow:var(--os-elev-3,0 24px 60px rgba(0,0,0,.55));color:var(--os-text,#fff);overflow:hidden}
.os-modal-h{padding:var(--os-space-5,16px) var(--os-space-6,24px);font-size:var(--os-text-lg,15px);font-weight:650;letter-spacing:-.01em;border-bottom:1px solid var(--os-border-subtle,rgba(255,255,255,.08))}
.os-modal-b{padding:var(--os-space-3,8px) var(--os-space-4,12px);display:flex;flex-direction:column}
.os-modal-f{padding:var(--os-space-4,12px) var(--os-space-5,16px);display:flex;justify-content:flex-end;gap:var(--os-space-3,8px);border-top:1px solid var(--os-border-subtle,rgba(255,255,255,.08))}
.os-pref-row{display:flex;align-items:center;justify-content:space-between;gap:var(--os-space-4,12px);padding:var(--os-space-2,6px);border-radius:var(--os-radius-md,10px);min-height:32px}
.os-pref-row+.os-pref-row{border-top:1px solid var(--os-border-subtle,rgba(255,255,255,.08))}
.os-pref-row .lab{font-size:var(--os-text-md,13px);opacity:.9}
/* overflow:hidden clips the segments' corners into the group, which also
   clips an outer focus ring — so segments ring themselves inset. */
.os-seg{display:inline-flex;border:1px solid var(--os-border-default,rgba(255,255,255,.14));border-radius:var(--os-radius-md,10px);overflow:hidden}
.os-seg button{min-height:32px;border:none;background:transparent;color:inherit;padding:0 var(--os-space-4,12px);font:var(--os-text-base,12px) var(--os-font-ui,system-ui);cursor:pointer;transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-seg button:hover{background:var(--os-hover,rgba(255,255,255,.08))}
.os-seg button:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-seg button:disabled{opacity:.45;cursor:not-allowed}
.os-seg button:focus-visible{outline:none;box-shadow:inset 0 0 0 2px var(--os-accent,#0a84ff)}
.os-seg button.on{background:var(--os-accent,#0a84ff);color:#fff;font-weight:600}
.os-seg button.on:hover{background:var(--os-accent,#0a84ff);filter:brightness(1.08)}
/* ── One switch, two class names ──────────────────────────────────────────
   The kit shipped two: .os-switch (a <button aria-pressed>, knob drawn with
   ::after, no focus style at all) and .os-field-switch (a <span> wrapping a
   real checkbox, knob a real element, focus ring present). Same job, two
   shapes to keep in sync, and only one of them was reachable by keyboard.
   Both class names stay — modules select on them — but they now resolve to
   one rule set, so there is one geometry and one ring to maintain. */
.os-switch,.os-field-switch{position:relative;flex:none;flex-shrink:0;width:38px;height:22px;border-radius:var(--os-radius-full,999px);background:var(--os-surface-3,rgba(255,255,255,.16));border:none;padding:0;cursor:pointer;transition:background var(--os-dur-base,160ms) var(--os-ease-out,ease),box-shadow var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-switch.on,.os-field-switch.is-on{background:var(--os-accent,#0a84ff)}
.os-switch::after{content:""}
.os-switch::after,.os-field-knob{position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:#fff;box-shadow:var(--os-elev-1,0 2px 8px rgba(0,0,0,.28));transition:transform var(--os-dur-base,160ms) var(--os-ease-spring,ease);pointer-events:none}
.os-switch.on::after,.os-field-switch.is-on .os-field-knob{transform:translateX(16px)}
/* A 38×22 track is under the 32px floor, so both variants grow the target
   vertically with a hit area: the <button> gets a ::before (::after is
   already the knob), the checkbox variant simply lets its invisible input
   overhang the track. */
.os-switch::before{content:"";position:absolute;inset:-5px -2px}
.os-field-check{position:absolute;inset:-5px -2px;width:auto;height:auto;margin:0;opacity:0;cursor:pointer}
/* Ring the track, not the (visually hidden) native checkbox — revealing the
   checkbox on focus would draw it on top of the switch. */
.os-field-check:focus-visible{outline:none}
.os-switch:focus-visible,.os-field-switch:has(.os-field-check:focus-visible){outline:none;box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.35))}
/* Disabled, at the .45/not-allowed the rest of the kit uses. The two variants
   need two selectors: .os-switch is the <button> and takes :disabled itself,
   but .os-field-switch is a <span> — nothing is disabled on it, so it has to
   ask its checkbox, the same way its focus ring does. Both also drop the
   pointer cursor off the label-sized hit areas (::before, .os-field-check),
   which sit outside the track and would otherwise still read as clickable. */
.os-switch:disabled,.os-field-switch:has(.os-field-check:disabled){opacity:.45;cursor:not-allowed}
.os-switch:disabled::before,.os-field-switch:has(.os-field-check:disabled) .os-field-check{cursor:not-allowed}
.os-step{display:inline-flex;align-items:center;gap:var(--os-space-4,12px)}
/* 24px was the smallest target in the prefs sheet and the one people miss
   while nudging a value repeatedly. */
.os-step button{width:32px;height:32px;border-radius:var(--os-radius-md,10px);border:1px solid var(--os-border-default,rgba(255,255,255,.14));background:var(--os-surface-2,rgba(255,255,255,.06));color:inherit;cursor:pointer;font-size:var(--os-text-lg,15px);line-height:1;transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-step button:hover{background:var(--os-hover,rgba(255,255,255,.08))}
.os-step button:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-step button:disabled{opacity:.45;cursor:not-allowed}
.os-step button:focus-visible{outline:none;border-color:var(--os-accent,#0a84ff);box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-step .v{min-width:40px;text-align:center;font:var(--os-text-base,12px) var(--os-font-mono,ui-monospace,monospace)}
.os-aci{display:flex;flex-direction:column;width:100%;max-width:100%;min-width:0;border:1px solid var(--os-glass-border,rgba(255,255,255,.14));background:rgba(0,0,0,.30);border-radius:var(--os-radius-xl,18px);overflow:hidden;transition:border-color var(--os-dur-base,160ms) var(--os-ease-out,ease),box-shadow var(--os-dur-base,160ms) var(--os-ease-out,ease)}
/* The composer wrapper carries the ring for its textarea: the input is
   full-bleed inside a clipped rounded box and has nowhere to draw one. */
.os-aci:focus-within{border-color:var(--os-accent,#0a84ff);box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
/* width:100% + min-width:0 — a bare textarea carries an intrinsic ~20col width
   that otherwise forces the composer wider than its pane. */
.os-aci-input{width:100%;min-width:0;border:none;background:transparent;color:var(--os-text,#fff);font:var(--os-text-md,13px)/1.55 var(--os-font-ui,system-ui);padding:var(--os-space-5,16px) var(--os-space-5,16px) var(--os-space-3,8px);resize:none;outline:none;min-height:26px;max-height:200px;overflow-y:auto}
.os-aci-input::placeholder{color:var(--os-text-faint,rgba(255,255,255,.4))}
.os-aci-bar{display:flex;align-items:center;gap:var(--os-space-3,8px);padding:var(--os-space-2,6px) var(--os-space-4,12px) var(--os-space-4,12px);min-width:0}
.os-aci-left,.os-aci-right{display:flex;align-items:center;gap:var(--os-space-2,6px);min-width:0}
.os-aci-left{flex:1 1 auto}
/* Must be allowed to shrink: it holds a nowrap model label, and with the
   default min-width:auto a long one pushes the send button off the edge. */
.os-aci-right{flex:0 1 auto}
.os-aci-act{display:inline-grid;place-items:center;width:32px;height:32px;flex:none;border:none;border-radius:var(--os-radius-md,10px);background:transparent;color:inherit;cursor:pointer;opacity:.72;transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease),opacity var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-aci-act:hover{background:var(--os-hover,rgba(255,255,255,.08));opacity:1}
.os-aci-act:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-aci-act:disabled{opacity:.3;cursor:not-allowed}
.os-aci-act:focus-visible{outline:none;opacity:1;box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-aci-act svg{width:16px;height:16px}
.os-aci-chip{display:inline-flex;align-items:center;gap:5px;font-size:var(--os-text-sm,11px);opacity:.6;padding:2px var(--os-space-2,6px);border-radius:var(--os-radius-sm,6px);background:var(--os-surface-1,rgba(255,255,255,.03));min-width:0;max-width:220px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis}
.os-aci-chip svg{width:13px;height:13px;flex:none}
.os-aci-mode{display:inline-flex;align-items:center;gap:5px;flex:0 1 auto;min-width:0;max-width:200px;min-height:32px;font-size:var(--os-text-sm,11px);opacity:.75;padding:0 var(--os-space-3,8px);border-radius:var(--os-radius-md,10px);background:transparent;border:none;color:inherit;cursor:pointer;white-space:nowrap;transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease),opacity var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-aci-mode:hover{background:var(--os-hover,rgba(255,255,255,.08));opacity:1}
.os-aci-mode:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-aci-mode:focus-visible{outline:none;opacity:1;box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-aci-mode svg{width:14px;height:14px;flex:none}
/* text-overflow needs a block box — the button itself is a flex container */
.os-aci-mode span{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.os-aci-send{position:relative;display:inline-grid;place-items:center;width:32px;height:32px;border:none;border-radius:var(--os-radius-md,10px);background:var(--os-accent,#0a84ff);color:#fff;cursor:pointer;transition:filter var(--os-dur-fast,120ms) var(--os-ease-out,ease),transform var(--os-dur-fast,120ms) var(--os-ease-out,ease),background var(--os-dur-base,160ms) var(--os-ease-out,ease);flex:none}
.os-aci-send:hover{filter:brightness(1.08)}
.os-aci-send:active{transform:scale(.95);filter:brightness(.94)}
.os-aci-send:disabled{opacity:.4;cursor:not-allowed}
.os-aci-send:focus-visible{outline:none;box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.35))}
.os-aci-send svg{width:16px;height:16px}
.os-aci-send.busy{background:var(--os-surface-3,rgba(255,255,255,.14))}
.os-aci-send.busy::before{content:"";position:absolute;inset:-3px;border-radius:13px;border:2px solid transparent;border-top-color:var(--os-accent,#0a84ff);animation:osSpin .7s linear infinite}
@keyframes osSpin{to{transform:rotate(360deg)}}
.os-cmdp-scrim{position:fixed;inset:0;z-index:420;display:flex;justify-content:center;align-items:flex-start;padding-top:12vh;background:rgba(0,0,0,.34);backdrop-filter:blur(var(--os-blur-sm,6px));animation:osFade var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-cmdp{width:min(620px,92vw);max-height:min(62vh,520px);display:flex;flex-direction:column;border-radius:var(--os-radius-lg,14px);border:1px solid var(--os-border-default,rgba(255,255,255,.14));background:var(--os-glass-bg-heavy,rgba(28,30,38,.94));backdrop-filter:blur(var(--os-blur-md,28px)) saturate(160%);-webkit-backdrop-filter:blur(var(--os-blur-md,28px)) saturate(160%);box-shadow:var(--os-elev-3,0 24px 60px rgba(0,0,0,.55));overflow:hidden;color:var(--os-text,#e8eaef)}
.os-cmdp-in{min-height:32px;box-sizing:border-box;border:none;border-bottom:1px solid var(--os-border-subtle,rgba(255,255,255,.08));background:transparent;color:inherit;padding:var(--os-space-4,12px) var(--os-space-5,16px);font:var(--os-text-md,13px) var(--os-font-ui,system-ui);outline:none}
/* This field had outline:none and no replacement — the palette's only input,
   and the one place a keyboard user always lands first. Inset, because the
   palette clips. */
.os-cmdp-in:focus-visible{outline:none;border-bottom-color:var(--os-accent,#0a84ff);box-shadow:inset 0 -1px 0 0 var(--os-accent,#0a84ff)}
.os-cmdp-in::placeholder{color:var(--os-text-faint,rgba(255,255,255,.4))}
.os-cmdp-list{flex:1;overflow:auto;padding:var(--os-space-2,6px)}
.os-cmdp-group{padding:var(--os-space-3,8px) var(--os-space-4,12px) var(--os-space-1,4px);font-size:var(--os-text-xs,10px);font-weight:700;letter-spacing:.06em;text-transform:uppercase;opacity:.45}
/* The row is driven from the keyboard (.on follows the query), but it is
   also clickable, so it takes the pointer cursor and the 32px floor. */
.os-cmdp-row{display:flex;align-items:center;gap:var(--os-space-3,8px);min-height:32px;padding:var(--os-space-2,6px) var(--os-space-4,12px);border-radius:var(--os-radius-md,10px);cursor:pointer}
.os-cmdp-row:hover{background:var(--os-hover,rgba(255,255,255,.08))}
.os-cmdp-row.on{background:var(--os-accent-soft,rgba(10,132,255,.28))}
.os-cmdp-row .ico{width:16px;height:16px;display:grid;place-items:center;flex:none;opacity:.8}
.os-cmdp-row .lab{flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:var(--os-text-md,13px)}
.os-cmdp-row .hint{flex:none;max-width:42%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-size:var(--os-text-sm,11px);opacity:.45}
.os-cmdp-row .sc{flex:none;font:var(--os-text-xs,10px) var(--os-font-mono,ui-monospace,monospace);opacity:.5;padding:2px var(--os-space-2,6px);border-radius:5px;background:var(--os-surface-2,rgba(255,255,255,.06))}
.os-cmdp-none{padding:var(--os-space-6,24px);text-align:center;opacity:.5;font-size:var(--os-text-base,12px)}
.os-tabs{display:flex;flex-direction:row;align-items:stretch;gap:3px;min-width:0;width:100%;padding:var(--os-space-1,4px) var(--os-space-2,6px) 0;overflow-x:auto;overflow-y:hidden;scrollbar-width:none;border-bottom:1px solid var(--os-border-subtle,rgba(255,255,255,.08));background:rgba(0,0,0,.16)}
.os-tabs::-webkit-scrollbar{height:0}
/* 32px tall: the strip scrolls horizontally with overflow hidden on the
   cross axis, so a tab cannot be taller than the strip and its children
   cannot ring outward — hence the inset rings below. */
.os-tab{position:relative;display:inline-flex;flex:0 0 auto;align-items:center;gap:var(--os-space-2,6px);max-width:210px;min-width:0;padding:0 var(--os-space-2,6px) 0 var(--os-space-4,12px);height:32px;border:1px solid transparent;border-bottom:none;border-radius:var(--os-radius-md,10px) var(--os-radius-md,10px) 0 0;background:transparent;color:inherit;font:var(--os-text-sm,11px) var(--os-font-ui,system-ui);cursor:pointer;opacity:.62;transition:background var(--os-dur-fast,120ms) var(--os-ease-out,ease),opacity var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-tab:hover{background:var(--os-surface-2,rgba(255,255,255,.06));opacity:.9}
.os-tab.is-active{background:var(--os-surface-3,rgba(255,255,255,.1));border-color:var(--os-border-subtle,rgba(255,255,255,.08));opacity:1}
.os-tab:focus-visible{outline:none;opacity:1;box-shadow:inset 0 0 0 2px var(--os-accent,#0a84ff)}
/* the active tab visually joins the editor below it */
.os-tab.is-active::after{content:"";position:absolute;left:0;right:0;bottom:-1px;height:1px;background:var(--os-surface-3,rgba(255,255,255,.1))}
.os-tab svg{width:13px;height:13px;flex:none;opacity:.75}
.os-tab .os-tab-label{min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.os-tab.is-dirty .os-tab-label{font-style:italic}
/* The worst target in the OS: 17×17 and opacity:0 until the tab was hovered,
   so it was invisible to anyone not holding a mouse. Now it is always
   visible (dimmed), paints at 24px so it still fits a 32px tab, and carries
   a 32×32 hit area on a pseudo-element. The 4px of horizontal bleed stays
   inside the 6px gap before the label, so it never steals a label click.
   Its ring is inset because the tab strip clips. */
.os-tab-close{position:relative;display:inline-grid;place-items:center;width:24px;height:24px;flex:none;border:none;border-radius:var(--os-radius-sm,6px);background:transparent;color:inherit;cursor:pointer;opacity:.45;transition:opacity var(--os-dur-fast,120ms) var(--os-ease-out,ease),background var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-tab-close::before{content:"";position:absolute;inset:-4px}
.os-tab-close svg{width:11px;height:11px;opacity:.9}
.os-tab:hover .os-tab-close,.os-tab.is-active .os-tab-close{opacity:.75}
.os-tab-close:hover{background:var(--os-hover,rgba(255,255,255,.08));opacity:1}
.os-tab-close:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-tab-close:focus-visible{outline:none;opacity:1;box-shadow:inset 0 0 0 2px var(--os-accent,#0a84ff)}
/* unsaved: a dot that turns into the × on hover, like every good editor */
.os-tab-dot{display:block;width:7px;height:7px;border-radius:50%;background:var(--os-accent,#0a84ff)}
.os-tab.is-dirty .os-tab-close{opacity:1}
.os-tab.is-dirty:hover .os-tab-dot{display:none}
.os-tab.is-dirty:not(:hover) .os-tab-close svg{display:none}
.os-tab.is-dirty:hover .os-tab-close svg{display:block}
/* Keyboard gets the same swap hover does: focus the close button on a dirty
   tab and the dot becomes the × you are about to press. */
.os-tab.is-dirty .os-tab-close:focus-visible .os-tab-dot{display:none}
.os-tab.is-dirty .os-tab-close:focus-visible svg{display:block}
.os-tab:not(.is-dirty) .os-tab-dot{display:none}
.os-statusbar{display:flex;align-items:center;gap:1px;padding:0 var(--os-space-2,6px);min-height:26px;border-top:1px solid var(--os-border-subtle,rgba(255,255,255,.08));background:rgba(0,0,0,.22);font:var(--os-text-sm,11px) var(--os-font-ui,system-ui);color:var(--os-text,#e8eaef);user-select:none;flex:none}
.os-sb-spacer{flex:1}
/* The one place the 32px floor does not apply: a status bar taller than its
   own text is not a status bar, and a hit area overhanging the bar would sit
   over app content. Clickable items get the full bar height (26px), a
   pointer cursor and a ring instead. */
.os-sb-item{display:inline-flex;align-items:center;gap:5px;padding:2px var(--os-space-2,6px);border:none;border-radius:var(--os-radius-sm,6px);background:transparent;color:inherit;font:inherit;opacity:.66;max-width:340px;overflow:hidden;white-space:nowrap;text-overflow:ellipsis;cursor:default}
.os-sb-item svg{width:13px;height:13px;flex:none}
.os-sb-item.is-clickable{cursor:pointer;min-height:24px}
.os-sb-item.is-clickable:hover{background:var(--os-hover,rgba(255,255,255,.08));opacity:1}
.os-sb-item.is-clickable:active{background:var(--os-active,rgba(255,255,255,.14))}
.os-sb-item.is-clickable:focus-visible{outline:none;opacity:1;box-shadow:inset 0 0 0 2px var(--os-accent,#0a84ff)}
.os-sb-item.ok{color:var(--os-success,#30d158);opacity:.9}
.os-sb-item.warn{color:var(--os-warn,#ffd60a);opacity:.95}
.os-sb-item.err{color:var(--os-danger,#ff453a);opacity:.95}
.os-sb-item.accent{color:var(--os-accent,#0a84ff);opacity:.95}
/* ── Form controls (slider / colorPicker / toggle / select / number / text) ──
   Every selector below is scoped under .os-field*: this stylesheet is injected
   into the document head, so a bare input{} / label{} / select{} rule would
   restyle the whole OS. Never add unscoped element selectors here. */
.os-field{display:flex;flex-direction:column;gap:5px;min-width:0;font:var(--os-text-md,13px) var(--os-font-ui,system-ui);color:var(--os-text,#e8eaef)}
.os-field-label{font-size:var(--os-text-sm,11px);font-weight:560;letter-spacing:-.01em;opacity:.72}
.os-field-row{display:flex;align-items:center;gap:var(--os-space-3,8px);min-width:0}
.os-field-input,.os-field-select,.os-field-hex{min-width:0;min-height:32px;box-sizing:border-box;border:1px solid var(--os-border-default,rgba(255,255,255,.14));background:var(--os-surface-2,rgba(255,255,255,.06));color:inherit;border-radius:var(--os-radius-md,10px);padding:var(--os-space-2,6px) var(--os-space-3,8px);outline:none;transition:border-color var(--os-dur-fast,120ms) var(--os-ease-out,ease),box-shadow var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-field-input,.os-field-select{flex:1;font:var(--os-text-md,13px) var(--os-font-ui,system-ui)}
/* mono: this one holds a hex code, where column alignment is the point */
.os-field-hex{flex:1;font:var(--os-text-sm,11px) var(--os-font-mono,ui-monospace,SFMono-Regular,monospace)}
.os-field-input:hover,.os-field-select:hover,.os-field-hex:hover{border-color:var(--os-border-strong,rgba(255,255,255,.22))}
.os-field-input:focus,.os-field-select:focus,.os-field-hex:focus,.os-field-input:focus-visible,.os-field-select:focus-visible,.os-field-hex:focus-visible{outline:none;border-color:var(--os-accent,#0a84ff);box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-field-input::placeholder{color:var(--os-text-faint,rgba(255,255,255,.38))}
.os-field-input:disabled,.os-field-select:disabled{opacity:.45;cursor:not-allowed}
.os-field-input:disabled:hover,.os-field-select:disabled:hover{border-color:var(--os-border-default,rgba(255,255,255,.14))}
/* The chevron was two linear-gradient triangles butted together — a 2010
   trick that could not be a real chevron shape and drifted whenever the
   padding changed. It is one inline SVG now. The stroke is a literal white:
   a data: URI is a separate document and cannot read currentColor or an
   --os-* token, and the alternatives (mask-image on the <select>, a pseudo
   element) either wash the option text or are not rendered on <select> at
   all. A light theme will need to override background-image here. */
.os-field-select{appearance:none;-webkit-appearance:none;cursor:pointer;padding-right:28px;background-image:url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12' fill='none' stroke='%23ffffff' stroke-opacity='.6' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M3 4.75 6 7.75 9 4.75'/%3E%3C/svg%3E");background-position:right var(--os-space-3,8px) center;background-size:12px 12px;background-repeat:no-repeat}
/* The popup list is drawn by the platform and needs an opaque fill, so it
   reads --os-window-body (the OS's opaque content surface) rather than the
   glass tints — the old #1c1e26 was invisible-on-invisible in a light theme. */
.os-field-select option{background:var(--os-window-body,#1c1e26);color:var(--os-text,#e8eaef)}
/* The slider used to be a 4px-tall element, which is also all the pointer
   had to hit. The element is 32px now and the 4px bar is the track
   pseudo-element, so the visual is unchanged and the target is 8× taller. */
.os-field-range{flex:1;min-width:0;appearance:none;-webkit-appearance:none;height:32px;background:transparent;outline:none;cursor:pointer}
.os-field-range::-webkit-slider-runnable-track{height:4px;border-radius:var(--os-radius-full,999px);background:var(--os-surface-3,rgba(255,255,255,.16))}
.os-field-range::-moz-range-track{height:4px;border-radius:var(--os-radius-full,999px);background:var(--os-surface-3,rgba(255,255,255,.16))}
.os-field-range::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;margin-top:-5px;border-radius:50%;background:#fff;box-shadow:var(--os-elev-1,0 2px 8px rgba(0,0,0,.28));cursor:pointer}
.os-field-range::-moz-range-thumb{width:14px;height:14px;border:none;border-radius:50%;background:#fff;box-shadow:var(--os-elev-1,0 2px 8px rgba(0,0,0,.28));cursor:pointer}
.os-field-range:disabled{opacity:.45;cursor:not-allowed}
/* Ring the thumb, not the element — the element is now 32px of mostly empty
   space and a ring around it would float unattached to anything. */
.os-field-range:focus-visible::-webkit-slider-thumb{box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-field-range:focus-visible::-moz-range-thumb{box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
.os-field-readout{flex:none;min-width:46px;text-align:right;font:var(--os-text-sm,11px) var(--os-font-mono,ui-monospace,SFMono-Regular,monospace);opacity:.62;white-space:nowrap}
.os-field-color{flex:none;width:36px;height:32px;padding:0;border:1px solid var(--os-border-strong,rgba(255,255,255,.22));border-radius:var(--os-radius-sm,6px);background:transparent;cursor:pointer}
.os-field-color::-webkit-color-swatch-wrapper{padding:2px}
.os-field-color::-webkit-color-swatch{border:none;border-radius:var(--os-radius-sm,6px)}
.os-field-color:hover{border-color:var(--os-accent,#0a84ff)}
.os-field-color:focus-visible{outline:none;border-color:var(--os-accent,#0a84ff);box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.28))}
/* gap is 16px so that two neighbouring 32px hit areas meet without
   overlapping — at the old 6px they would have fought over 8px of pointer. */
.os-field-chips{display:flex;flex-wrap:wrap;gap:var(--os-space-5,16px)}
/* A swatch bigger than ~18px stops reading as a swatch, so the paint stays
   and the target is a pseudo-element around it. */
.os-field-chip{position:relative;width:18px;height:18px;padding:0;border:1px solid var(--os-border-strong,rgba(255,255,255,.22));border-radius:50%;cursor:pointer;transition:box-shadow var(--os-dur-fast,120ms) var(--os-ease-out,ease)}
.os-field-chip::after{content:"";position:absolute;inset:-7px;border-radius:50%}
.os-field-chip.is-on{box-shadow:0 0 0 2px var(--os-accent,#0a84ff)}
.os-field-chip:focus-visible{outline:none;box-shadow:0 0 0 3px var(--os-accent-soft,rgba(10,132,255,.35))}
.os-field-chip.is-on:focus-visible{box-shadow:0 0 0 2px var(--os-accent,#0a84ff),0 0 0 5px var(--os-accent-soft,rgba(10,132,255,.35))}
/* Disabled, matching the rest of the kit. The ::after hit area is 7px of
   invisible target around the swatch, so it needs the cursor too — otherwise
   the pointer reads "clickable" everywhere except the 18px it lands on. */
.os-field-chip:disabled{opacity:.45;cursor:not-allowed}
.os-field-chip:disabled::after{cursor:not-allowed}
.os-field--toggle{flex-direction:row;align-items:center;justify-content:space-between;gap:var(--os-space-4,12px)}
/* ── Reduced motion ──────────────────────────────────────────────────────
   The blanket reduction lives in styles/os.css (universal + !important, so it
   reaches this sheet even though this one is injected later). What it cannot
   do is decide what a stopped indicator should look like, and this kit owns
   the only infinite animation in it.
   .os-aci-send.busy::before is a ring with three transparent sides that reads
   as "working" purely because it rotates; capped at one iteration it becomes a
   single arc frozen at an arbitrary angle, which reads as a rendering bug. A
   complete ring — soft all the way round, solid at the top — is a static badge
   that still says busy, and it sits on a button that is disabled meanwhile. */
@media (prefers-reduced-motion:reduce){
.os-aci-send.busy::before{animation:none;border-color:var(--os-accent-soft,rgba(10,132,255,.28));border-top-color:var(--os-accent,#0a84ff)}
/* The send button's press feedback is a 5% shrink. Instantaneous it is a
   flinch rather than a press; brightness alone carries the state. */
.os-aci-send:active{transform:none}
}
`;
  document.head.appendChild(s);
}

/**
 * Normalise a user-typed colour to canonical `#rrggbb`, or null when it is not
 * a valid hex colour. Accepts `abc`, `#abc`, `AABBCC`, `#aabbcc`.
 */
function normalizeHex(v: string | null | undefined): string | null {
  if (!v) return null;
  let s = v.trim();
  if (!s.startsWith("#")) s = "#" + s;
  if (/^#[0-9a-f]{3}$/i.test(s)) s = "#" + s[1] + s[1] + s[2] + s[2] + s[3] + s[3];
  return /^#[0-9a-f]{6}$/i.test(s) ? s.toLowerCase() : null;
}

let fieldSeq = 0;

/**
 * Rank a command against a lowercased query: a label prefix beats a label
 * substring, which beats a hit anywhere in the haystack (group/keywords/hint),
 * which beats a scattered subsequence. Returns -1 for no match, 0 for "".
 * Exported for tests — the palette itself is DOM-bound.
 */
export function scoreCommand(cmd: UiCommand, q: string): number {
  if (!q) return 0;
  const hay = (cmd.label + " " + (cmd.group || "") + " " + (cmd.keywords || "") + " " + (cmd.hint || ""))
    .toLowerCase();
  const label = cmd.label.toLowerCase();
  const idx = label.indexOf(q);
  if (idx === 0) return 1000;
  if (idx > 0) return 800 - idx;
  const sub = hay.indexOf(q);
  if (sub >= 0) return 600 - Math.min(sub, 200);
  // scattered subsequence over the label, then the whole haystack
  let score = -1;
  for (const target of [label, hay]) {
    let ti = 0;
    let hits = 0;
    for (const ch of q) {
      const at = target.indexOf(ch, ti);
      if (at < 0) {
        hits = -1;
        break;
      }
      ti = at + 1;
      hits++;
    }
    if (hits > 0) {
      score = (target === label ? 400 : 200) - (ti - hits);
      break;
    }
  }
  return score;
}

export function createOsUiApi(icons?: IconsApi) {
  ensureCss();

  function tree(host: HTMLElement, opts: UiTreeOptions = {}): UiTreeHandle {
    ensureCss();
    const root = document.createElement("div");
    root.className = "os-tree" + (opts.className ? " " + opts.className : "");
    root.tabIndex = 0;
    host.appendChild(root);

    let nodes: UiTreeNode[] = [];
    let selected: string | null = null;
    const expanded = new Map<string, boolean>();
    const byId = new Map<string, UiTreeNode>();
    const indent = opts.indent ?? 14;

    const index = (list: UiTreeNode[]) => {
      byId.clear();
      const walk = (ns: UiTreeNode[]) => {
        for (const n of ns) {
          byId.set(n.id, n);
          if (n.children?.length) {
            if (!expanded.has(n.id)) expanded.set(n.id, n.expanded !== false);
            walk(n.children);
          }
        }
      };
      walk(list);
    };

    const render = () => {
      root.innerHTML = "";
      const flash = new Set(opts.flashIds || []);
      const paint = (list: UiTreeNode[], depth: number) => {
        for (const n of list) {
          const isFolder =
            Array.isArray(n.children) || String(n.id).startsWith("dir:");
          const hasKids = isFolder;
          const open = isFolder && expanded.get(n.id) !== false;
          const row = document.createElement("div");
          row.className =
            "os-tree-row" +
            (selected === n.id ? " is-selected" : "") +
            (flash.has(n.id) ? " is-flash" : "");
          row.style.paddingLeft = 4 + depth * indent + "px";
          row.dataset.id = n.id;

          const twist = document.createElement("button");
          twist.type = "button";
          twist.className = "os-tree-twist" + (hasKids ? "" : " is-leaf");
          twist.textContent = hasKids ? (open ? "▼" : "▶") : "";
          twist.onclick = (e) => {
            e.stopPropagation();
            if (!hasKids) return;
            expanded.set(n.id, !open);
            render();
          };

          const ico = document.createElement("span");
          ico.className = "os-tree-ico";
          const iconName =
            n.icon ||
            (hasKids ? (open ? "folder-open" : "folder") : "file");
          if (icons) {
            try {
              ico.appendChild(
                icons.forEntry
                  ? icons.forEntry(
                      {
                        name: n.label,
                        type: hasKids ? "dir" : "file",
                      },
                      { size: 14 }
                    )
                  : icons.create(iconName, { size: 14 })
              );
            } catch {
              /* */
            }
          }

          const lab = document.createElement("span");
          lab.className = "os-tree-label";
          lab.textContent = n.label;

          row.append(twist, ico, lab);
          row.onclick = () => {
            selected = n.id;
            render();
            opts.onSelect?.(n.id, n);
          };
          row.ondblclick = () => {
            if (hasKids) {
              expanded.set(n.id, !open);
              render();
            } else {
              opts.onActivate?.(n.id, n);
            }
          };
          row.oncontextmenu = (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            opts.onContextMenu?.(n.id, n, ev as unknown as MouseEvent);
          };
          root.appendChild(row);
          if (isFolder && open && n.children && n.children.length) paint(n.children, depth + 1);
        }
      };
      paint(nodes, 0);
    };

    return {
      el: root,
      setData(ns: UiTreeNode[]) {
        nodes = ns || [];
        index(nodes);
        render();
      },
      getSelected: () => selected,
      setSelected(id: string | null) {
        selected = id;
        render();
      },
      expand(id: string, open = true) {
        expanded.set(id, open);
        render();
      },
      destroy() {
        root.remove();
      },
    };
  }

  function split(
    host: HTMLElement,
    panes: HTMLElement[],
    opts: UiSplitOptions = {}
  ): UiSplitHandle {
    ensureCss();
    const dir = opts.direction || "horizontal";
    const root = document.createElement("div");
    root.className =
      "os-split is-" + dir + (opts.className ? " " + opts.className : "");
    host.appendChild(root);

    let sizes =
      opts.sizes && opts.sizes.length === panes.length
        ? [...opts.sizes]
        : panes.map(() => 1);
    if (opts.storageKey) {
      try {
        const raw = localStorage.getItem("arclog.split." + opts.storageKey);
        if (raw) {
          const parsed = JSON.parse(raw) as number[];
          if (parsed.length === panes.length) sizes = parsed;
        }
      } catch {
        /* */
      }
    }
    const min = opts.minSize ?? 120;
    const paneEls: HTMLElement[] = [];

    root.innerHTML = "";
    panes.forEach((content, i) => {
      const pane = document.createElement("div");
      pane.className = "os-split-pane";
      pane.appendChild(content);
      root.appendChild(pane);
      paneEls.push(pane);
      if (i < panes.length - 1) {
        const g = document.createElement("div");
        g.className = "os-split-gutter";
        root.appendChild(g);
        let start = 0;
        let startSizes = sizes.slice();
        const onMove = (ev: PointerEvent) => {
          const rect = root.getBoundingClientRect();
          const delta =
            dir === "horizontal"
              ? ev.clientX - start
              : ev.clientY - start;
          const span =
            dir === "horizontal" ? rect.width : rect.height;
          const gutters = panes.length - 1;
          const usable = Math.max(1, span - gutters * 5);
          // Convert delta pixels into relative size units matching sizes[] scale
          const unitTotal = startSizes.reduce((a, b) => a + b, 0) || 1;
          const dUnit = (delta / usable) * unitTotal;
          let a = startSizes[i] + dUnit;
          let b = startSizes[i + 1] - dUnit;
          const minUnit = (min / usable) * unitTotal;
          if (a < minUnit) {
            b -= minUnit - a;
            a = minUnit;
          }
          if (b < minUnit) {
            a -= minUnit - b;
            b = minUnit;
          }
          if (a < minUnit || b < minUnit) return;
          sizes[i] = a;
          sizes[i + 1] = b;
          applyClean();
          opts.onResize?.(sizes.slice());
        };
        const onUp = () => {
          g.classList.remove("is-active");
          window.removeEventListener("pointermove", onMove);
          window.removeEventListener("pointerup", onUp);
          if (opts.storageKey) {
            localStorage.setItem(
              "arclog.split." + opts.storageKey,
              JSON.stringify(sizes)
            );
          }
        };
        g.onpointerdown = (ev) => {
          ev.preventDefault();
          g.classList.add("is-active");
          start = dir === "horizontal" ? ev.clientX : ev.clientY;
          startSizes = sizes.slice();
          window.addEventListener("pointermove", onMove);
          window.addEventListener("pointerup", onUp);
        };
      }
    });

    function applyClean() {
      const total = sizes.reduce((a, b) => a + b, 0) || 1;
      paneEls.forEach((p, i) => {
        /**
         * Grow-based sizing, NOT percentages.
         *
         * `flex: 0 0 <pct>%` sums the panes to exactly 100% while each gutter
         * adds a real 5px on top, and nothing is allowed to shrink — so the
         * track overflows its container by 5px per gutter and the last pane
         * hangs off the edge, clipped by the window. Nested splits stack the
         * error (Studio's agent pane lost ~10px of its right side, which read
         * as "the composer has no right padding").
         *
         * With basis 0 and proportional grow, the free space — container width
         * minus the gutters — is divided in the sizes[] ratio, which is also
         * exactly what the drag maths assumes (`usable = span - gutters*5`).
         */
        p.style.flex = sizes[i] / total + " 1 0";
        if (dir === "horizontal") {
          p.style.width = "";
          p.style.height = "100%";
        } else {
          p.style.height = "";
          p.style.width = "100%";
        }
      });
    }
    applyClean();

    return {
      el: root,
      setSizes(s: number[]) {
        if (s.length === panes.length) {
          sizes = s.slice();
          applyClean();
        }
      },
      getSizes: () => sizes.slice(),
      destroy() {
        root.remove();
      },
    };
  }

  function searchSelect(
    host: HTMLElement,
    opts: UiSearchSelectOptions
  ): UiSearchSelectHandle {
    ensureCss();
    let options = opts.options.slice();
    let value = opts.value || options[0]?.value || "";
    const wrap = document.createElement("div");
    wrap.className = "os-sselect" + (opts.className ? " " + opts.className : "");
    host.appendChild(wrap);

    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "os-sselect-btn";
    wrap.appendChild(btn);

    let pop: HTMLElement | null = null;
    let activeIdx = 0;

    const labelFor = (v: string) => {
      const o = options.find((x) => x.value === v);
      return o?.label || v || opts.placeholder || "Select…";
    };

    const paintBtn = () => {
      btn.innerHTML =
        '<span class="val"></span><span class="chev">▾</span>';
      (btn.querySelector(".val") as HTMLElement).textContent = labelFor(value);
    };

    const close = () => {
      pop?.remove();
      pop = null;
      window.removeEventListener("mousedown", onDoc, true);
    };

    const onDoc = (e: MouseEvent) => {
      if (!wrap.contains(e.target as Node)) close();
    };

    const open = () => {
      if (pop) {
        close();
        return;
      }
      pop = document.createElement("div");
      pop.className = "os-sselect-pop";
      const search = document.createElement("input");
      search.className = "os-sselect-search";
      search.placeholder = "Search models…";
      const list = document.createElement("div");
      list.className = "os-sselect-list";
      pop.append(search, list);
      wrap.appendChild(pop);
      window.addEventListener("mousedown", onDoc, true);

      const renderList = (q: string) => {
        const qq = q.trim().toLowerCase();
        const filtered = !qq
          ? options
          : options.filter(
              (o) =>
                o.label.toLowerCase().includes(qq) ||
                o.value.toLowerCase().includes(qq) ||
                (o.hint || "").toLowerCase().includes(qq)
            );
        list.innerHTML = "";
        if (!filtered.length) {
          list.innerHTML = '<div class="os-sselect-empty">No matches</div>';
          return;
        }
        let lastGroup = "";
        filtered.forEach((o, i) => {
          if (o.group && o.group !== lastGroup) {
            lastGroup = o.group;
            const g = document.createElement("div");
            g.className = "os-sselect-group";
            g.textContent = o.group;
            list.appendChild(g);
          }
          const b = document.createElement("button");
          b.type = "button";
          b.className =
            "os-sselect-opt" + (i === activeIdx || o.value === value ? " is-active" : "");
          b.innerHTML =
            '<span class="lab"></span>' +
            (o.hint || o.value !== o.label
              ? '<span class="hint"></span>'
              : "");
          (b.querySelector(".lab") as HTMLElement).textContent = o.label;
          const h = b.querySelector(".hint") as HTMLElement | null;
          if (h) h.textContent = o.hint || o.value;
          b.onmousedown = (e) => e.preventDefault();
          b.onclick = () => {
            value = o.value;
            paintBtn();
            opts.onChange?.(value, o);
            close();
          };
          list.appendChild(b);
        });
      };

      renderList("");
      setTimeout(() => search.focus(), 0);
      search.oninput = () => {
        activeIdx = 0;
        renderList(search.value);
      };
      search.onkeydown = (e) => {
        const q = search.value.trim().toLowerCase();
        const filtered = !q
          ? options
          : options.filter(
              (o) =>
                o.label.toLowerCase().includes(q) ||
                o.value.toLowerCase().includes(q)
            );
        if (e.key === "ArrowDown") {
          e.preventDefault();
          activeIdx = Math.min(filtered.length - 1, activeIdx + 1);
          renderList(search.value);
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          activeIdx = Math.max(0, activeIdx - 1);
          renderList(search.value);
        } else if (e.key === "Enter") {
          e.preventDefault();
          const o = filtered[activeIdx];
          if (o) {
            value = o.value;
            paintBtn();
            opts.onChange?.(value, o);
            close();
          }
        } else if (e.key === "Escape") {
          close();
        }
      };
    };

    btn.onclick = () => open();
    paintBtn();

    return {
      el: wrap,
      getValue: () => value,
      setValue(v: string) {
        value = v;
        paintBtn();
      },
      setOptions(optsIn: UiSearchSelectOption[]) {
        options = optsIn.slice();
        if (!options.find((o) => o.value === value)) {
          value = options[0]?.value || "";
        }
        paintBtn();
        if (pop) {
          close();
        }
      },
      destroy() {
        close();
        wrap.remove();
      },
    };
  }

  /**
   * Attach standard code-editing keybindings to a <textarea>. Reusable across
   * any app that edits text (used internally by codeEditor). Edits go through
   * execCommand("insertText") so the browser's native undo stack is preserved
   * and the textarea's own "input" listeners fire. Returns a disposer.
   *
   * Bindings (⌘ on macOS, Ctrl elsewhere):
   *   ⌘D            select word under caret; again → next occurrence
   *   Alt+↑ / ↓     move line(s) up / down
   *   ⇧Alt+↑ / ↓    duplicate line(s) up / down
   *   ⌘⇧D           duplicate line down
   *   ⌘⇧K           delete line(s)
   *   ⌘/            toggle line comment (code languages)
   *   Tab / ⇧Tab    indent / outdent (multi-line aware)
   * Native macOS caret nav (⌘←/→ line, ⌥←/→ word, ⌘↑/↓ document) is untouched.
   * Note: a <textarea> has a single selection, so ⌘D advances one selection
   * rather than adding simultaneous cursors (that needs a full editor engine).
   */
  function editorKeys(
    ta: HTMLTextAreaElement,
    opts: { comment?: string | null | (() => string | null); indent?: string } = {}
  ): () => void {
    const INDENT = opts.indent || "  ";
    const commentTok = (): string | null =>
      typeof opts.comment === "function" ? opts.comment() : opts.comment ?? "//";
    const v = () => ta.value;
    const lineStartAt = (pos: number) => v().lastIndexOf("\n", pos - 1) + 1;
    const lineEndAt = (pos: number) => {
      const i = v().indexOf("\n", pos);
      return i === -1 ? v().length : i;
    };
    const selLines = () => ({
      s: lineStartAt(ta.selectionStart),
      e: lineEndAt(ta.selectionEnd),
    });

    // Replace [start,end) with text, preserving undo; then set the new selection.
    function apply(start: number, end: number, text: string, selStart: number, selEnd = selStart) {
      ta.focus();
      ta.setSelectionRange(start, end);
      let ok = false;
      try {
        ok = document.execCommand("insertText", false, text);
      } catch {
        ok = false;
      }
      if (!ok) {
        ta.setRangeText(text, start, end, "end");
        ta.dispatchEvent(new Event("input", { bubbles: true }));
      }
      ta.setSelectionRange(selStart, selEnd);
    }

    function selectWordOrNext() {
      const s = v();
      const a = ta.selectionStart;
      const b = ta.selectionEnd;
      if (a === b) {
        const w = /[A-Za-z0-9_$]/;
        let l = a;
        let r = a;
        while (l > 0 && w.test(s[l - 1])) l--;
        while (r < s.length && w.test(s[r])) r++;
        if (l !== r) ta.setSelectionRange(l, r);
        return;
      }
      const sel = s.slice(a, b);
      let idx = s.indexOf(sel, b);
      if (idx === -1) idx = s.indexOf(sel, 0); // wrap around
      if (idx !== -1) ta.setSelectionRange(idx, idx + sel.length);
    }

    function moveLines(dir: -1 | 1) {
      const s = v();
      const { s: bs, e: be } = selLines();
      const block = s.slice(bs, be);
      if (dir === -1) {
        if (bs === 0) return;
        const prevStart = lineStartAt(bs - 1);
        const prev = s.slice(prevStart, bs - 1);
        apply(
          prevStart,
          be,
          block + "\n" + prev,
          prevStart + (ta.selectionStart - bs),
          prevStart + (ta.selectionEnd - bs)
        );
      } else {
        if (be >= s.length) return;
        const nextEnd = lineEndAt(be + 1);
        const next = s.slice(be + 1, nextEnd);
        const shift = next.length + 1;
        apply(bs, nextEnd, next + "\n" + block, ta.selectionStart + shift, ta.selectionEnd + shift);
      }
    }

    function duplicateLines(dir: -1 | 1) {
      const s = v();
      const { s: bs, e: be } = selLines();
      const block = s.slice(bs, be);
      if (dir === 1) {
        const off = block.length + 1;
        apply(be, be, "\n" + block, ta.selectionStart + off, ta.selectionEnd + off);
      } else {
        apply(bs, bs, block + "\n", ta.selectionStart, ta.selectionEnd);
      }
    }

    function deleteLines() {
      const s = v();
      let { s: bs, e: be } = selLines();
      if (be < s.length) be += 1;
      else if (bs > 0) bs -= 1;
      apply(bs, be, "", bs, bs);
    }

    function indent(outdent: boolean) {
      const s = v();
      const { s: bs, e: be } = selLines();
      const lines = s.slice(bs, be).split("\n");
      let head = 0;
      let total = 0;
      const next = lines
        .map((ln, i) => {
          if (outdent) {
            const m = ln.match(/^(\t| {1,2})/);
            const cut = m ? m[0].length : 0;
            if (i === 0) head = -cut;
            total -= cut;
            return ln.slice(cut);
          }
          if (ln === "") return ln;
          if (i === 0) head = INDENT.length;
          total += INDENT.length;
          return INDENT + ln;
        })
        .join("\n");
      apply(
        bs,
        be,
        next,
        Math.max(bs, ta.selectionStart + head),
        Math.max(bs, ta.selectionEnd + total)
      );
    }

    function tabKey(shift: boolean) {
      const a = ta.selectionStart;
      const b = ta.selectionEnd;
      if (shift) {
        indent(true);
      } else if (a !== b && v().slice(a, b).includes("\n")) {
        indent(false);
      } else {
        apply(a, b, INDENT, a + INDENT.length);
      }
    }

    function toggleComment() {
      const tok = commentTok();
      if (!tok) return;
      const s = v();
      const { s: bs, e: be } = selLines();
      const lines = s.slice(bs, be).split("\n");
      const nonEmpty = lines.filter((l) => l.trim() !== "");
      const allOn = nonEmpty.length > 0 && nonEmpty.every((l) => l.trimStart().startsWith(tok));
      const next = lines
        .map((ln) => {
          if (ln.trim() === "") return ln;
          const lead = ln.match(/^\s*/)?.[0] ?? "";
          if (allOn) {
            let rest = ln.slice(lead.length + tok.length);
            if (rest.startsWith(" ")) rest = rest.slice(1);
            return lead + rest;
          }
          return lead + tok + " " + ln.slice(lead.length);
        })
        .join("\n");
      apply(bs, be, next, bs, bs + next.length);
    }

    const handler = (e: KeyboardEvent) => {
      const mod = e.metaKey || e.ctrlKey;
      const key = e.key;
      if (mod && !e.altKey && key.toLowerCase() === "d") {
        e.preventDefault();
        if (e.shiftKey) duplicateLines(1);
        else selectWordOrNext();
        return;
      }
      if (e.altKey && (key === "ArrowUp" || key === "ArrowDown")) {
        e.preventDefault();
        if (e.shiftKey) duplicateLines(key === "ArrowDown" ? 1 : -1);
        else moveLines(key === "ArrowUp" ? -1 : 1);
        return;
      }
      if (mod && e.shiftKey && key.toLowerCase() === "k") {
        e.preventDefault();
        deleteLines();
        return;
      }
      if (mod && !e.shiftKey && !e.altKey && key === "/") {
        e.preventDefault();
        toggleComment();
        return;
      }
      if (key === "Tab") {
        e.preventDefault();
        tabKey(e.shiftKey);
      }
    };
    ta.addEventListener("keydown", handler);
    return () => ta.removeEventListener("keydown", handler);
  }

  // codeEditor is powered by CodeMirror 6 (see ./code-editor). Kept on ctx.ui so
  // every app shares one engine — real multi-cursor (⌘D), move/duplicate/delete
  // line, comment toggle, history, and the shared editor-preferences popover.
  function codeEditor(
    host: HTMLElement,
    opts: {
      value?: string;
      language?: string;
      readOnly?: boolean;
      placeholder?: string;
      className?: string;
      onChange?: (value: string) => void;
      onSave?: (value: string) => void;
    } = {}
  ): CodeEditorHandle {
    ensureCss();
    return createCodeEditor(host, opts);
  }

  function richText(
    host: HTMLElement,
    opts: {
      value?: string;
      html?: string;
      placeholder?: string;
      className?: string;
      toolbar?: boolean;
      onChange?: (html: string, text: string) => void;
    } = {}
  ) {
    ensureCss();
    host.innerHTML = "";
    const root = document.createElement("div");
    root.className = "os-rte" + (opts.className ? " " + opts.className : "");
    const showTb = opts.toolbar !== false;
    let tb: HTMLElement | null = null;
    if (showTb) {
      tb = document.createElement("div");
      tb.className = "os-rte-tb";
      const mk = (label: string, cmd: string, val?: string) => {
        const b = document.createElement("button");
        b.type = "button";
        b.textContent = label;
        b.title = cmd;
        b.onmousedown = (e) => e.preventDefault();
        b.onclick = () => {
          body.focus();
          document.execCommand(cmd, false, val);
          emit();
        };
        return b;
      };
      const sep = () => {
        const s = document.createElement("span");
        s.className = "sep";
        return s;
      };
      tb.append(
        mk("B", "bold"),
        mk("I", "italic"),
        mk("U", "underline"),
        sep(),
        mk("H1", "formatBlock", "h1"),
        mk("H2", "formatBlock", "h2"),
        mk("P", "formatBlock", "p"),
        sep(),
        mk("•", "insertUnorderedList"),
        mk("1.", "insertOrderedList"),
        sep(),
        mk("S", "strikeThrough"),
        mk("</>", "removeFormat")
      );
      root.appendChild(tb);
    }
    const body = document.createElement("div");
    body.className = "os-rte-body";
    body.contentEditable = "true";
    body.dataset.placeholder = opts.placeholder || "Start writing…";
    const initial = opts.html ?? opts.value ?? "";
    if (initial && /[<>]/.test(initial)) body.innerHTML = initial;
    else if (initial) body.textContent = initial;
    const emit = () => opts.onChange?.(body.innerHTML, body.innerText || "");
    body.addEventListener("input", emit);
    root.appendChild(body);
    host.appendChild(root);
    return {
      el: root,
      getHtml: () => body.innerHTML,
      setHtml(html: string) {
        body.innerHTML = html;
      },
      getText: () => body.innerText || "",
      setText(text: string) {
        body.textContent = text;
      },
      exec(command: string, value?: string) {
        body.focus();
        document.execCommand(command, false, value);
        emit();
      },
      /* Same shape as codeEditor.undo/redo so callers don't branch on which
         editor they were handed. contenteditable keeps its own native stack. */
      undo(): boolean {
        body.focus();
        const ok = document.execCommand("undo");
        emit();
        return ok;
      },
      redo(): boolean {
        body.focus();
        const ok = document.execCommand("redo");
        emit();
        return ok;
      },
      focus: () => body.focus(),
      destroy() {
        root.remove();
      },
    };
  }


  function sidebar(
    host: HTMLElement,
    opts: {
      items: Array<{
        id: string;
        label: string;
        icon?: string;
        group?: string;
        hint?: string;
        disabled?: boolean;
        visible?: boolean;
      }>;
      value?: string;
      width?: number;
      className?: string;
      onChange?: (id: string, item: any) => void;
    }
  ) {
    ensureCss();
    host.innerHTML = "";
    let items = (opts.items || []).filter((i) => i.visible !== false);
    let value = opts.value || items.find((i) => !i.disabled)?.id || "";
    let filter = "";

    const root = document.createElement("div");
    root.className = "os-sidebar" + (opts.className ? " " + opts.className : "");
    root.style.width = (opts.width || 200) + "px";
    root.style.minWidth = (opts.width || 200) + "px";

    const search = document.createElement("input");
    search.className = "os-sidebar-search";
    search.type = "search";
    search.placeholder = "Search";
    search.setAttribute("aria-label", "Filter sidebar");
    root.appendChild(search);

    const list = document.createElement("div");
    list.style.cssText = "flex:1;min-height:0;overflow:auto";
    root.appendChild(list);
    host.appendChild(root);

    const paint = () => {
      list.innerHTML = "";
      const q = filter.trim().toLowerCase();
      const visible = items.filter((i) => {
        if (!q) return true;
        return (
          i.label.toLowerCase().includes(q) ||
          i.id.toLowerCase().includes(q) ||
          (i.group || "").toLowerCase().includes(q)
        );
      });
      let lastGroup = "";
      for (const it of visible) {
        const g = it.group || "";
        if (g && g !== lastGroup) {
          const gh = document.createElement("div");
          gh.className = "os-sidebar-group";
          gh.textContent = g;
          list.appendChild(gh);
          lastGroup = g;
        }
        const b = document.createElement("button");
        b.type = "button";
        b.className =
          "os-sidebar-item" +
          (it.id === value ? " is-on" : "") +
          (it.disabled ? " is-disabled" : "");
        b.disabled = !!it.disabled;
        const ico = document.createElement("span");
        ico.className = "ico";
        if (it.icon && icons) {
          try {
            ico.appendChild(icons.create(it.icon, { size: 15 }));
          } catch {
            ico.textContent = "";
          }
        }
        const lab = document.createElement("span");
        lab.className = "lab";
        lab.textContent = it.label;
        b.append(ico, lab);
        if (it.hint) {
          const h = document.createElement("span");
          h.className = "hint";
          h.textContent = it.hint;
          b.appendChild(h);
        }
        b.onclick = () => {
          if (it.disabled) return;
          value = it.id;
          paint();
          opts.onChange?.(it.id, it);
        };
        list.appendChild(b);
      }
      if (!visible.length) {
        const empty = document.createElement("div");
        empty.style.cssText = "padding:16px 8px;opacity:.45;font-size:12px";
        empty.textContent = "No sections";
        list.appendChild(empty);
      }
    };

    search.oninput = () => {
      filter = search.value;
      paint();
    };
    paint();

    return {
      el: root,
      getValue: () => value,
      setValue(id: string) {
        value = id;
        paint();
      },
      setItems(next: typeof items) {
        items = (next || []).filter((i) => i.visible !== false);
        if (!items.find((i) => i.id === value)) {
          value = items.find((i) => !i.disabled)?.id || "";
        }
        paint();
      },
      destroy() {
        root.remove();
      },
    };
  }


  function selectable(el: HTMLElement, opts: { enabled?: boolean } = {}) {
    const on = opts.enabled !== false;
    if (on) {
      el.classList.add("os-selectable");
      el.classList.remove("os-no-select");
      el.setAttribute("data-os-selectable", "1");
      el.removeAttribute("data-os-no-select");
      el.style.userSelect = "text";
    } else {
      el.classList.remove("os-selectable");
      el.classList.add("os-no-select");
      el.removeAttribute("data-os-selectable");
      el.setAttribute("data-os-no-select", "1");
      el.style.userSelect = "none";
    }
  }

  /** OS-styled button element. The SDK's canonical button — apps should use
   * this instead of hand-rolling <button> markup. */
  function button(opts: {
    label?: string;
    icon?: string;
    variant?: "default" | "primary" | "danger" | "ghost";
    title?: string;
    disabled?: boolean;
    className?: string;
    onClick?: (e: MouseEvent) => void;
  } = {}): HTMLButtonElement {
    ensureCss();
    const b = document.createElement("button");
    b.type = "button";
    b.className =
      "os-btn os-btn-" + (opts.variant || "default") + (opts.className ? " " + opts.className : "");
    if (opts.title) b.title = opts.title;
    if (opts.disabled) b.disabled = true;
    if (opts.icon && icons) {
      try {
        b.appendChild(icons.create(opts.icon, { size: 15 }));
      } catch {
        /* unknown icon — skip */
      }
    }
    if (opts.label) {
      const span = document.createElement("span");
      span.textContent = opts.label;
      b.appendChild(span);
    }
    if (opts.onClick) b.addEventListener("click", opts.onClick);
    return b;
  }

  /* ── Form controls ─────────────────────────────────────────────────────────
     Generic SDK components — any module can use them (widget settings is just
     the first consumer). All styling lives in ensureCss() under .os-field*. */

  /** Wrapper + optional <label> preceding the control's input. */
  function fieldShell(kind: string, label: string | undefined, inputId: string): HTMLElement {
    const root = document.createElement("div");
    // BEM modifier (`--`), NOT `os-field-<kind>`: that form collides with the
    // ELEMENT classes below — `.os-field-color` is the swatch input and
    // `.os-field-select` is the <select>. A colliding root inherited the input's
    // width/height/border and even the select's background-image chevron, which
    // squeezed the whole field into a 34x26 box and drew a second chevron.
    root.className = "os-field os-field--" + kind;
    if (label) {
      const l = document.createElement("label");
      l.className = "os-field-label";
      l.htmlFor = inputId;
      l.textContent = label;
      root.appendChild(l);
    }
    return root;
  }

  function nextFieldId(prefix: string): string {
    fieldSeq += 1;
    return "os-" + prefix + "-" + fieldSeq;
  }

  /** OS-styled range slider with a live readout (+ optional unit suffix). */
  function slider(opts: UiSliderOptions): UiValueHandle<number> {
    ensureCss();
    const id = nextFieldId("sl");
    const root = fieldShell("slider", opts.label, id);
    const row = document.createElement("div");
    row.className = "os-field-row";
    const input = document.createElement("input");
    input.type = "range";
    input.id = id;
    input.className = "os-field-range";
    input.min = String(opts.min);
    input.max = String(opts.max);
    if (opts.step != null) input.step = String(opts.step);
    input.value = String(opts.value != null ? opts.value : opts.min);
    const readout = document.createElement("span");
    readout.className = "os-field-readout";
    const paint = () => {
      readout.textContent = input.value + (opts.unit || "");
    };
    paint();
    row.appendChild(input);
    row.appendChild(readout);
    root.appendChild(row);

    // `input` (not `change`) so consumers get live feedback while dragging.
    const onInput = () => {
      paint();
      opts.onChange?.(Number(input.value));
    };
    input.addEventListener("input", onInput);

    return {
      el: root,
      getValue: () => Number(input.value),
      setValue(v: number) {
        input.value = String(v);
        paint();
      },
      destroy() {
        input.removeEventListener("input", onInput);
        root.remove();
      },
    };
  }

  /** Colour swatch + hex field (kept in sync) with optional preset chips. */
  function colorPicker(opts: UiColorOptions): UiValueHandle<string> {
    ensureCss();
    const id = nextFieldId("col");
    const root = fieldShell("color", opts.label, id);
    const row = document.createElement("div");
    row.className = "os-field-row";
    const swatch = document.createElement("input");
    swatch.type = "color";
    swatch.id = id;
    swatch.className = "os-field-color";
    const hex = document.createElement("input");
    hex.type = "text";
    hex.className = "os-field-hex";
    hex.spellcheck = false;
    hex.maxLength = 7;
    hex.setAttribute("aria-label", (opts.label || "Colour") + " hex value");

    let value = normalizeHex(opts.value) || "#000000";
    swatch.value = value;
    hex.value = value;
    row.appendChild(swatch);
    row.appendChild(hex);
    root.appendChild(row);

    let chipRow: HTMLElement | null = null;

    function paintChips() {
      if (!chipRow) return;
      for (const c of Array.from(chipRow.children) as HTMLElement[])
        c.classList.toggle("is-on", (c.dataset.color || "") === value);
    }

    const apply = (next: string, syncHexText: boolean) => {
      value = next;
      swatch.value = next;
      if (syncHexText) hex.value = next;
      paintChips();
      opts.onChange?.(next);
    };

    const onSwatch = () => apply(swatch.value.toLowerCase(), true);
    // Ignore half-typed / invalid hex rather than clobbering the value. While
    // TYPING only a full 6-digit "#rrggbb" commits — shorthand is deliberately
    // NOT accepted here, because typing "#ff0000" passes through "#ff0", which
    // is itself valid 3-digit hex and would expand to #ffff00 and fire a
    // spurious onChange. Consumers (the widget settings dialog) persist changes
    // live, so that would save a colour the user never picked. Shorthand is
    // still accepted on blur, when the entry is unambiguously finished.
    const onHexInput = () => {
      const raw = hex.value.trim();
      const withHash = raw.startsWith("#") ? raw : "#" + raw;
      if (withHash.length !== 7) return;
      const v = normalizeHex(withHash);
      if (v) apply(v, false);
    };
    // On blur the entry is final: accept shorthand too, then snap the text to
    // the canonical value (or back to the last good one if it never parsed).
    const onHexBlur = () => {
      const v = normalizeHex(hex.value);
      if (v && v !== value) apply(v, false);
      hex.value = value;
    };
    const onChipClick = (e: MouseEvent) => {
      const t = (e.target as HTMLElement | null)?.closest(".os-field-chip") as HTMLElement | null;
      if (!t) return;
      const c = normalizeHex(t.dataset.color);
      if (c) apply(c, true);
    };
    swatch.addEventListener("input", onSwatch);
    hex.addEventListener("input", onHexInput);
    hex.addEventListener("blur", onHexBlur);

    if (opts.swatches && opts.swatches.length) {
      chipRow = document.createElement("div");
      chipRow.className = "os-field-chips";
      for (const s of opts.swatches) {
        const c = normalizeHex(s);
        if (!c) continue;
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "os-field-chip";
        chip.dataset.color = c;
        chip.style.background = c;
        chip.title = c;
        chipRow.appendChild(chip);
      }
      chipRow.addEventListener("click", onChipClick);
      root.appendChild(chipRow);
      paintChips();
    }

    return {
      el: root,
      getValue: () => value,
      setValue(v: string) {
        const c = normalizeHex(v);
        if (!c) return;
        value = c;
        swatch.value = c;
        hex.value = c;
        paintChips();
      },
      destroy() {
        swatch.removeEventListener("input", onSwatch);
        hex.removeEventListener("input", onHexInput);
        hex.removeEventListener("blur", onHexBlur);
        chipRow?.removeEventListener("click", onChipClick);
        root.remove();
      },
    };
  }

  /** On/off switch (a checkbox styled as a track + knob). */
  function toggle(opts: UiToggleOptions): UiValueHandle<boolean> {
    ensureCss();
    const id = nextFieldId("tg");
    const root = fieldShell("toggle", opts.label, id);
    const track = document.createElement("span");
    track.className = "os-field-switch";
    const input = document.createElement("input");
    input.type = "checkbox";
    input.id = id;
    input.className = "os-field-check";
    input.checked = !!opts.checked;
    if (!opts.label) input.setAttribute("aria-label", "Toggle");
    const knob = document.createElement("span");
    knob.className = "os-field-knob";
    track.appendChild(input);
    track.appendChild(knob);
    root.appendChild(track);
    const paint = () => track.classList.toggle("is-on", input.checked);
    paint();

    const onChange = () => {
      paint();
      opts.onChange?.(input.checked);
    };
    input.addEventListener("change", onChange);

    return {
      el: root,
      getValue: () => input.checked,
      setValue(v: boolean) {
        input.checked = !!v;
        paint();
      },
      destroy() {
        input.removeEventListener("change", onChange);
        root.remove();
      },
    };
  }

  /** Plain dropdown for short option lists (use searchSelect for long ones). */
  function select(opts: UiSelectOptions): UiSelectHandle {
    ensureCss();
    const id = nextFieldId("sel");
    const root = fieldShell("select", opts.label, id);
    const el = document.createElement("select");
    el.id = id;
    el.className = "os-field-select";
    root.appendChild(el);

    const build = (list: UiSelectOption[], keep: string | undefined) => {
      el.textContent = "";
      for (const o of list) {
        const op = document.createElement("option");
        op.value = o.value;
        op.textContent = o.label;
        el.appendChild(op);
      }
      // Preserve the current selection when it still exists in the new list.
      if (keep != null && list.some((o) => o.value === keep)) el.value = keep;
    };
    build(opts.options || [], opts.value);

    const onChange = () => opts.onChange?.(el.value);
    el.addEventListener("change", onChange);

    return {
      el: root,
      getValue: () => el.value,
      setValue(v: string) {
        el.value = v;
      },
      setOptions(o: UiSelectOption[]) {
        build(o || [], el.value);
      },
      destroy() {
        el.removeEventListener("change", onChange);
        root.remove();
      },
    };
  }

  /** Numeric input, clamped to min/max on commit. */
  function numberField(opts: UiNumberOptions): UiValueHandle<number> {
    ensureCss();
    const id = nextFieldId("num");
    const root = fieldShell("number", opts.label, id);
    const input = document.createElement("input");
    input.type = "number";
    input.id = id;
    input.className = "os-field-input";
    if (opts.min != null) input.min = String(opts.min);
    if (opts.max != null) input.max = String(opts.max);
    if (opts.step != null) input.step = String(opts.step);
    input.value = String(opts.value != null ? opts.value : (opts.min != null ? opts.min : 0));
    root.appendChild(input);

    const clamp = (n: number) => {
      let v = Number.isFinite(n) ? n : opts.min != null ? opts.min : 0;
      if (opts.min != null && v < opts.min) v = opts.min;
      if (opts.max != null && v > opts.max) v = opts.max;
      return v;
    };
    const onChange = () => {
      const v = clamp(Number(input.value));
      input.value = String(v);
      opts.onChange?.(v);
    };
    input.addEventListener("change", onChange);

    return {
      el: root,
      getValue: () => clamp(Number(input.value)),
      setValue(v: number) {
        input.value = String(clamp(v));
      },
      destroy() {
        input.removeEventListener("change", onChange);
        root.remove();
      },
    };
  }

  /** Single-line text input. */
  function textField(opts: UiTextOptions): UiValueHandle<string> {
    ensureCss();
    const id = nextFieldId("txt");
    const root = fieldShell("text", opts.label, id);
    const input = document.createElement("input");
    input.type = "text";
    input.id = id;
    input.className = "os-field-input";
    if (opts.placeholder) input.placeholder = opts.placeholder;
    if (opts.maxLength != null) input.maxLength = opts.maxLength;
    input.value = opts.value != null ? opts.value : "";
    root.appendChild(input);

    // Deliberately `change` (blur / Enter), NOT `input`: firing per keystroke
    // would kick off one network request per typed character for settings like
    // a weather widget's location.
    const onChange = () => opts.onChange?.(input.value);
    input.addEventListener("change", onChange);

    return {
      el: root,
      getValue: () => input.value,
      setValue(v: string) {
        input.value = v != null ? v : "";
      },
      destroy() {
        input.removeEventListener("change", onChange);
        root.remove();
      },
    };
  }

  /** Modal editor-preferences dialog (opened from an app's menu item). Edits the
   * shared editorPrefs store, which every open codeEditor reconfigures live. */
  function openEditorPrefsDialog(): void {
    ensureCss();
    const scrim = document.createElement("div");
    scrim.className = "os-modal-scrim";
    const modal = document.createElement("div");
    modal.className = "os-modal";
    scrim.appendChild(modal);

    const close = () => {
      document.removeEventListener("keydown", onKey);
      scrim.remove();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    scrim.addEventListener("mousedown", (e) => {
      if (e.target === scrim) close();
    });
    document.addEventListener("keydown", onKey);

    const head = document.createElement("div");
    head.className = "os-modal-h";
    head.textContent = "Editor Preferences";
    const bodyEl = document.createElement("div");
    bodyEl.className = "os-modal-b";
    const foot = document.createElement("div");
    foot.className = "os-modal-f";

    const renderBody = () => {
      const p = getEditorPrefs();
      bodyEl.innerHTML = "";

      // Tab size — segmented control
      const tabRow = document.createElement("div");
      tabRow.className = "os-pref-row";
      tabRow.innerHTML = `<span class="lab">Tab size</span>`;
      const seg = document.createElement("div");
      seg.className = "os-seg";
      for (const n of [2, 4, 8]) {
        const sb = document.createElement("button");
        sb.type = "button";
        sb.textContent = String(n);
        if (p.tabSize === n) sb.className = "on";
        sb.onclick = () => {
          setEditorPrefs({ tabSize: n });
          renderBody();
        };
        seg.appendChild(sb);
      }
      tabRow.appendChild(seg);
      bodyEl.appendChild(tabRow);

      // toggles
      const addToggle = (label: string, key: "wordWrap" | "lineNumbers") => {
        const row = document.createElement("div");
        row.className = "os-pref-row";
        row.innerHTML = `<span class="lab">${label}</span>`;
        const sw = document.createElement("button");
        sw.type = "button";
        sw.className = "os-switch" + (p[key] ? " on" : "");
        sw.setAttribute("aria-pressed", String(p[key]));
        sw.onclick = () => {
          setEditorPrefs({ [key]: !getEditorPrefs()[key] } as Partial<typeof p>);
          renderBody();
        };
        row.appendChild(sw);
        bodyEl.appendChild(row);
      };
      addToggle("Word wrap", "wordWrap");
      addToggle("Line numbers", "lineNumbers");

      // font size — stepper
      const fontRow = document.createElement("div");
      fontRow.className = "os-pref-row";
      fontRow.innerHTML = `<span class="lab">Font size</span>`;
      const step = document.createElement("div");
      step.className = "os-step";
      const minus = document.createElement("button");
      minus.type = "button";
      minus.textContent = "−";
      minus.onclick = () => {
        setEditorPrefs({ fontSize: getEditorPrefs().fontSize - 1 });
        renderBody();
      };
      const v = document.createElement("span");
      v.className = "v";
      v.textContent = p.fontSize + " px";
      const plus = document.createElement("button");
      plus.type = "button";
      plus.textContent = "+";
      plus.onclick = () => {
        setEditorPrefs({ fontSize: getEditorPrefs().fontSize + 1 });
        renderBody();
      };
      step.append(minus, v, plus);
      fontRow.appendChild(step);
      bodyEl.appendChild(fontRow);
    };
    renderBody();

    foot.appendChild(button({ label: "Done", variant: "primary", onClick: close }));

    modal.append(head, bodyEl, foot);
    document.body.appendChild(scrim);
  }

  /**
   * Claude-Code-style agent composer: an auto-growing input with an inline send
   * arrow, a left action row, an optional status chip + mode button, and a
   * busy→stop state. The canonical input for any agent/chat pane (Studio,
   * Hermes, future apps).
   */
  function agentChatInput(
    host: HTMLElement,
    opts: {
      placeholder?: string;
      value?: string;
      /** Enter submits (⇧Enter = newline). ⌘/Ctrl+Enter always submits. Default true. */
      submitOnEnter?: boolean;
      /** Left toolbar icon actions. */
      actions?: Array<{ id: string; icon: string; title?: string; onClick: () => void }>;
      /** Right-side mode/model chip (e.g. the selected model). */
      mode?: { label: string; icon?: string; onClick?: () => void };
      onSubmit?: (text: string) => void;
      onStop?: () => void;
    } = {}
  ) {
    ensureCss();
    host.innerHTML = "";
    let busy = false;
    const root = document.createElement("div");
    root.className = "os-aci";

    const ta = document.createElement("textarea");
    ta.className = "os-aci-input";
    ta.rows = 1;
    ta.placeholder = opts.placeholder || "Message the agent…";
    ta.value = opts.value || "";
    const grow = () => {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 200) + "px";
    };

    const bar = document.createElement("div");
    bar.className = "os-aci-bar";
    const left = document.createElement("div");
    left.className = "os-aci-left";
    const right = document.createElement("div");
    right.className = "os-aci-right";
    bar.append(left, right);

    const statusChip = document.createElement("span");
    statusChip.className = "os-aci-chip";
    statusChip.style.display = "none";

    const modeBtn = document.createElement("button");
    modeBtn.type = "button";
    modeBtn.className = "os-aci-mode";
    modeBtn.style.display = "none";

    const send = document.createElement("button");
    send.type = "button";
    send.className = "os-aci-send";
    send.title = "Send";
    const setSendIcon = () => {
      send.innerHTML = "";
      if (icons) {
        try {
          send.appendChild(icons.create(busy ? "square" : "arrow-up", { size: 16 }));
          return;
        } catch {
          /* fall through */
        }
      }
      send.textContent = busy ? "■" : "↑";
    };

    const submit = () => {
      const v = ta.value.trim();
      if (busy || !v) return;
      opts.onSubmit?.(v);
    };
    send.addEventListener("click", () => {
      if (busy) opts.onStop?.();
      else submit();
    });

    ta.addEventListener("input", grow);
    ta.addEventListener("keydown", (e) => {
      if (e.key !== "Enter") return;
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        submit();
      } else if (opts.submitOnEnter !== false && !e.shiftKey) {
        e.preventDefault();
        submit();
      }
    });

    function renderActions(actions?: typeof opts.actions) {
      left.innerHTML = "";
      for (const a of actions || []) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "os-aci-act";
        if (a.title) b.title = a.title;
        if (icons) {
          try {
            b.appendChild(icons.create(a.icon, { size: 16 }));
          } catch {
            b.textContent = "•";
          }
        }
        b.addEventListener("click", a.onClick);
        left.appendChild(b);
      }
      left.appendChild(statusChip);
    }

    function renderMode(mode?: typeof opts.mode) {
      if (!mode) {
        modeBtn.style.display = "none";
        return;
      }
      modeBtn.style.display = "";
      modeBtn.innerHTML = "";
      if (mode.icon && icons) {
        try {
          modeBtn.appendChild(icons.create(mode.icon, { size: 14 }));
        } catch {
          /* */
        }
      }
      const label = document.createElement("span");
      label.textContent = mode.label;
      modeBtn.appendChild(label);
      modeBtn.onclick = mode.onClick || null;
    }

    renderActions(opts.actions);
    renderMode(opts.mode);
    right.append(modeBtn, send);
    setSendIcon();
    root.append(ta, bar);
    host.appendChild(root);
    requestAnimationFrame(grow);

    return {
      el: root,
      getValue: () => ta.value,
      setValue(v: string) {
        ta.value = v;
        grow();
      },
      clear() {
        ta.value = "";
        grow();
      },
      focus: () => ta.focus(),
      setBusy(b: boolean) {
        busy = b;
        send.classList.toggle("busy", b);
        send.title = b ? "Stop" : "Send";
        setSendIcon();
      },
      setStatus(status: { icon?: string; label?: string } | null) {
        if (!status || !status.label) {
          statusChip.style.display = "none";
          return;
        }
        statusChip.style.display = "";
        statusChip.innerHTML = "";
        if (status.icon && icons) {
          try {
            statusChip.appendChild(icons.create(status.icon, { size: 13 }));
          } catch {
            /* */
          }
        }
        const label = document.createElement("span");
        label.textContent = status.label;
        statusChip.appendChild(label);
      },
      setActions(actions: typeof opts.actions) {
        renderActions(actions);
      },
      setMode(mode: typeof opts.mode) {
        renderMode(mode);
      },
      destroy() {
        root.remove();
      },
    };
  }

  /**
   * ⌘⇧P command palette — the OS's canonical "run any action" surface. Apps
   * register their commands once and get filtering, grouping, keyboard nav and
   * the hotkey for free. `onQuery` adds dynamic results (file search, symbols).
   */
  function commandPalette(opts: UiCommandPaletteOptions = {}): UiCommandPaletteHandle {
    ensureCss();
    let commands: UiCommand[] = opts.commands ? [...opts.commands] : [];
    let scrim: HTMLElement | null = null;
    let input: HTMLInputElement | null = null;
    let list: HTMLElement | null = null;
    let shown: UiCommand[] = [];
    let active = 0;
    let seq = 0;

    const render = () => {
      if (!list) return;
      list.innerHTML = "";
      if (!shown.length) {
        const none = document.createElement("div");
        none.className = "os-cmdp-none";
        none.textContent = opts.emptyText || "No matching commands";
        list.appendChild(none);
        return;
      }
      let group: string | undefined;
      shown.forEach((cmd, i) => {
        if (cmd.group && cmd.group !== group) {
          group = cmd.group;
          const h = document.createElement("div");
          h.className = "os-cmdp-group";
          h.textContent = cmd.group;
          list!.appendChild(h);
        }
        const row = document.createElement("div");
        row.className = "os-cmdp-row" + (i === active ? " on" : "");
        row.dataset.index = String(i);
        const ico = document.createElement("span");
        ico.className = "ico";
        if (cmd.icon && icons) {
          try {
            ico.appendChild(icons.create(cmd.icon, { size: 15 }));
          } catch {
            /* icon id may not exist */
          }
        }
        const lab = document.createElement("span");
        lab.className = "lab";
        lab.textContent = cmd.label;
        row.append(ico, lab);
        if (cmd.hint) {
          const hint = document.createElement("span");
          hint.className = "hint";
          hint.textContent = cmd.hint;
          row.appendChild(hint);
        }
        if (cmd.shortcut) {
          const sc = document.createElement("span");
          sc.className = "sc";
          sc.textContent = cmd.shortcut;
          row.appendChild(sc);
        }
        row.addEventListener("mousemove", () => {
          if (active === i) return;
          active = i;
          paintActive();
        });
        row.addEventListener("mousedown", (e) => {
          e.preventDefault();
          runAt(i);
        });
        list!.appendChild(row);
      });
    };

    const paintActive = () => {
      if (!list) return;
      const rows = list.querySelectorAll<HTMLElement>(".os-cmdp-row");
      rows.forEach((r) => r.classList.toggle("on", Number(r.dataset.index) === active));
      rows[active]?.scrollIntoView({ block: "nearest" });
    };

    const filter = async () => {
      const q = (input?.value || "").trim().toLowerCase();
      const mine = ++seq;
      const statics = commands
        .filter((c) => !c.disabled)
        .map((c) => ({ c, s: scoreCommand(c, q) }))
        .filter((x) => x.s >= 0)
        .sort((a, b) => b.s - a.s)
        .map((x) => x.c);
      let dynamic: UiCommand[] = [];
      if (opts.onQuery) {
        try {
          dynamic = (await opts.onQuery(q)) || [];
        } catch {
          dynamic = [];
        }
      }
      if (mine !== seq || !list) return; // a newer keystroke won
      shown = [...statics, ...dynamic].slice(0, 200);
      active = 0;
      render();
    };

    const runAt = (i: number) => {
      const cmd = shown[i];
      if (!cmd) return;
      close();
      try {
        cmd.run();
      } catch {
        /* a failing command must not take the palette down */
      }
    };

    function close() {
      scrim?.remove();
      scrim = null;
      input = null;
      list = null;
      shown = [];
    }

    function open(query = "") {
      if (scrim) {
        input?.focus();
        return;
      }
      scrim = document.createElement("div");
      scrim.className = "os-cmdp-scrim";
      const panel = document.createElement("div");
      panel.className = "os-cmdp";
      input = document.createElement("input");
      input.className = "os-cmdp-in";
      input.type = "text";
      input.spellcheck = false;
      input.placeholder = opts.placeholder || "Type a command…";
      input.value = query;
      list = document.createElement("div");
      list.className = "os-cmdp-list";
      panel.append(input, list);
      scrim.appendChild(panel);
      scrim.addEventListener("mousedown", (e) => {
        if (e.target === scrim) close();
      });
      input.addEventListener("input", () => void filter());
      input.addEventListener("keydown", (e) => {
        if (e.key === "ArrowDown") {
          e.preventDefault();
          active = Math.min(active + 1, shown.length - 1);
          paintActive();
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          active = Math.max(active - 1, 0);
          paintActive();
        } else if (e.key === "Enter") {
          e.preventDefault();
          runAt(active);
        } else if (e.key === "Escape") {
          e.preventDefault();
          close();
        }
      });
      document.body.appendChild(scrim);
      input.focus();
      input.select();
      void filter();
    }

    const onHotkey = (ev: Event) => {
      const e = ev as KeyboardEvent;
      if (!(e.metaKey || e.ctrlKey) || !e.shiftKey || e.altKey) return;
      if ((e.key || "").toLowerCase() !== "p" && e.code !== "KeyP") return;
      e.preventDefault();
      e.stopPropagation();
      toggle();
    };
    const hotkeyTarget = opts.hotkeyTarget || document;
    if (opts.hotkey !== false) hotkeyTarget.addEventListener("keydown", onHotkey, true);

    function toggle() {
      if (scrim) close();
      else open();
    }

    return {
      open,
      close,
      toggle,
      isOpen: () => !!scrim,
      setCommands(next: UiCommand[]) {
        commands = [...next];
        if (scrim) void filter();
      },
      destroy() {
        if (opts.hotkey !== false) hotkeyTarget.removeEventListener("keydown", onHotkey, true);
        close();
      },
    };
  }

  /**
   * Editor tab strip — the OS's canonical tabs. Handles the affordances every
   * app otherwise forgets: a close ×, middle-click to close, an unsaved dot
   * that turns into the × on hover, ellipsised labels, and horizontal overflow.
   */
  function tabs(host: HTMLElement, opts: UiTabsOptions = {}): UiTabsHandle {
    ensureCss();
    const root = document.createElement("div");
    root.className = "os-tabs" + (opts.className ? " " + opts.className : "");
    root.setAttribute("role", "tablist");
    host.appendChild(root);

    let list: UiTab[] = opts.tabs ? [...opts.tabs] : [];
    let active: string | null = opts.activeId ?? null;

    const render = () => {
      root.innerHTML = "";
      for (const tab of list) {
        const closable = tab.closable !== false && !!opts.onClose;
        const el = document.createElement("div");
        el.className =
          "os-tab" +
          (tab.id === active ? " is-active" : "") +
          (tab.dirty ? " is-dirty" : "");
        el.setAttribute("role", "tab");
        el.setAttribute("aria-selected", tab.id === active ? "true" : "false");
        if (tab.hint) el.title = tab.hint;

        if (tab.icon && icons) {
          try {
            el.appendChild(icons.create(tab.icon, { size: 13 }));
          } catch {
            /* unknown icon id — the label still reads */
          }
        }
        const label = document.createElement("span");
        label.className = "os-tab-label";
        label.textContent = tab.label;
        el.appendChild(label);

        if (closable) {
          const close = document.createElement("button");
          close.type = "button";
          close.className = "os-tab-close";
          // In the tab order. It used to be tabIndex -1 and invisible until
          // the tab was hovered, which meant a keyboard user could neither
          // see nor reach the only way to close a tab. The CSS keeps it
          // dimmed rather than hidden so that reaching it shows something.
          close.tabIndex = 0;
          close.setAttribute("aria-label", "Close " + tab.label);
          const dot = document.createElement("span");
          dot.className = "os-tab-dot";
          close.appendChild(dot);
          if (icons) {
            try {
              close.appendChild(icons.create("close", { size: 11 }));
            } catch {
              /* CSS falls back to the dot */
            }
          }
          close.addEventListener("mousedown", (e) => e.stopPropagation());
          close.addEventListener("click", (e) => {
            e.stopPropagation();
            opts.onClose?.(tab.id);
          });
          el.appendChild(close);
        }

        el.addEventListener("mousedown", (e) => {
          // Middle-click closes, and must not paste-on-Linux or autoscroll.
          if (e.button === 1) {
            e.preventDefault();
            if (closable) opts.onClose?.(tab.id);
            return;
          }
          if (e.button === 0 && tab.id !== active) opts.onSelect?.(tab.id);
        });
        root.appendChild(el);
      }
      // keep the active tab in view when the strip overflows
      const on = root.querySelector<HTMLElement>(".os-tab.is-active");
      on?.scrollIntoView({ block: "nearest", inline: "nearest" });
    };
    render();

    return {
      el: root,
      setTabs(next: UiTab[]) {
        list = [...next];
        if (active && !list.some((t) => t.id === active)) active = null;
        render();
      },
      setActive(id: string | null) {
        active = id;
        render();
      },
      getActive: () => active,
      destroy() {
        root.remove();
      },
    };
  }

  /**
   * IDE-style status bar — a thin strip of left/right aligned chips an app
   * updates by id (file, cursor position, problems, build state…). Chips with
   * an `onClick` render as buttons.
   */
  function statusBar(host: HTMLElement, opts: { items?: UiStatusBarItem[]; className?: string } = {}): UiStatusBarHandle {
    ensureCss();
    const root = document.createElement("div");
    root.className = "os-statusbar" + (opts.className ? " " + opts.className : "");
    host.appendChild(root);
    let items: UiStatusBarItem[] = opts.items ? [...opts.items] : [];
    /** id → live chip, so a caret move patches text instead of rebuilding. */
    const nodes = new Map<string, { chip: HTMLElement; label: HTMLElement; item: UiStatusBarItem }>();

    const toneClass = (item: UiStatusBarItem) =>
      "os-sb-item" +
      (item.tone && item.tone !== "default" ? " " + item.tone : "") +
      (item.onClick ? " is-clickable" : "");

    const render = () => {
      root.innerHTML = "";
      nodes.clear();
      const spacer = document.createElement("span");
      spacer.className = "os-sb-spacer";
      const paint = (item: UiStatusBarItem) => {
        const chip = document.createElement(item.onClick ? "button" : "span") as HTMLElement;
        if (item.onClick) (chip as HTMLButtonElement).type = "button";
        chip.className = toneClass(item);
        if (item.title) chip.title = item.title;
        if (item.icon && icons) {
          try {
            chip.appendChild(icons.create(item.icon, { size: 13 }));
          } catch {
            /* unknown icon id — chip still renders its label */
          }
        }
        const label = document.createElement("span");
        label.textContent = item.label;
        chip.appendChild(label);
        const rec = { chip, label, item };
        // Read the handler off `rec` so patched items keep the latest closure.
        if (item.onClick) chip.addEventListener("click", () => rec.item.onClick?.());
        nodes.set(item.id, rec);
        root.appendChild(chip);
      };
      for (const it of items) if (it.align !== "right") paint(it);
      root.appendChild(spacer);
      for (const it of items) if (it.align === "right") paint(it);
    };

    /** Same ids, same order, same structural fields → patch in place. */
    const canPatch = (next: UiStatusBarItem[]) =>
      next.length === items.length &&
      next.every((it, i) => {
        const cur = items[i];
        return (
          cur &&
          nodes.has(it.id) &&
          it.id === cur.id &&
          it.icon === cur.icon &&
          (it.align || "left") === (cur.align || "left") &&
          !!it.onClick === !!cur.onClick
        );
      });

    const patch = (next: UiStatusBarItem[]) => {
      for (const it of next) {
        const rec = nodes.get(it.id);
        if (!rec) continue;
        if (rec.label.textContent !== it.label) rec.label.textContent = it.label;
        const cls = toneClass(it);
        if (rec.chip.className !== cls) rec.chip.className = cls;
        const title = it.title || "";
        if (rec.chip.title !== title) rec.chip.title = title;
        rec.item = it;
      }
      items = [...next];
    };

    const apply = (next: UiStatusBarItem[]) => {
      if (canPatch(next)) patch(next);
      else {
        items = [...next];
        render();
      }
    };
    render();

    return {
      el: root,
      set(item: UiStatusBarItem) {
        const i = items.findIndex((x) => x.id === item.id);
        if (i < 0) {
          apply([...items, item]);
          return;
        }
        const next = [...items];
        next[i] = { ...items[i], ...item };
        apply(next);
      },
      setItems(next: UiStatusBarItem[]) {
        apply(next);
      },
      remove(id: string) {
        apply(items.filter((x) => x.id !== id));
      },
      destroy() {
        nodes.clear();
        root.remove();
      },
    };
  }

  // `satisfies UiApi` (not a `: UiApi` return annotation) so the SDK contract is
  // compiler-checked while internal callers keep the precise inferred type.
  return {
    tree,
    split,
    searchSelect,
    button,
    slider,
    colorPicker,
    toggle,
    select,
    numberField,
    textField,
    agentChatInput,
    commandPalette,
    statusBar,
    tabs,
    codeEditor,
    richText,
    editorKeys,
    editorPrefs: {
      get: getEditorPrefs,
      set: setEditorPrefs,
      subscribe: subscribeEditorPrefs,
      open: openEditorPrefsDialog,
    },
    sidebar,
    selectable,
    tooltip: createTooltipApi(),
    /**
     * markdown-lite: agent replies rendered safely (escape-first; see
     * markdown-lite.mjs). The SDK surface every chat/comms bubble must use.
     */
    markdown: renderMarkdown,
    ensureCss,
  } satisfies UiApi;

}

export type OsUiApi = ReturnType<typeof createOsUiApi>;
